/* eslint-disable react/prop-types */
// react/prop-types is redundant in this TypeScript file (props are validated by their type
// annotations) and, once the top-level component takes props, misfires on the plain `.map`/
// `.find` callbacks below (e.g. `(p) => p.id`), flagging them as components missing prop-types.
// Payouts simulator — entity-scoped money-movement dashboard.
//
// A faithful React port of the design artifact's dashboard simulator
// (usecases/Payouts simulator.html + simulator.js + simulator.css). Navigation is a
// dashboard — Overview / Internal accounts / External accounts / Payments —
// scoped to the business chosen in the sub-navbar entity switcher. It drives the *real*
// /v2 Payouts API end to end: create/select a business, add internal + external accounts,
// create a payment, then track and simulate its lifecycle — every call streamed into a
// request inspector.
//
// "Funding sources / delivery methods" from the artifact are surfaced here as *internal*
// and *external* accounts (they map to ownershipType=internal / ownershipType=external),
// which is the vocabulary the rest of this portal uses.
//
// Unlike the typed api/payouts.ts client, this screen needs to log every raw request/
// response into the inspector and reach a few endpoints the typed client doesn't model
// yet (GET /health, POST /simulations/payments/{id}/actions). So it carries its
// own thin `api()` wrapper — pointed at the portal's own environment config
// (config/environments.ts) rather than the artifact's hardcoded hosts — that adds the
// partner api-key, a per-POST Idempotency-Key, and the risk-session token, exactly like
// the real client does.
import { useState, useRef, useEffect, useCallback, type ReactNode, type CSSProperties } from 'react';

import { ENVIRONMENTS, apiBaseUrl, isEnvironmentId, type EnvironmentId } from '@/config/environments';
import { config } from '@/config';
import { getSonarSessionToken } from '@/lib/sonarSdk';
import { trackEvent, useFlowAnalytics } from '@/lib/analytics';
import { actionLabel, blockedReason, serverErrorHelp } from '@/lib/simulationActions';
import { useApp } from '@/components/ds';
import { TopNav } from '@/layout/TopNav';

import '@/styles/simulator.css';

// ---- Domain constants (verbatim from the design artifact) ------------------
// deliveryPreference is explicit per rail (payouts.api.external.yaml): each rail has a normal-speed
// value (`standard-ach`, `standard-check`, `domestic-wire`, `virtual-card`) alongside its faster
// variants, and every variant is its own enum value.
const PREFERENCES: Record<string, string[]> = {
  ach: ['standard-ach', 'same-day-ach', 'rtp'],
  check: ['standard-check', 'express-check', 'overnight-check'],
  'domestic-wire': ['domestic-wire', 'instant-domestic-wire'],
  'virtual-card': ['virtual-card', 'instant-virtual-card'],
};
// The API always returns Plaid-backed internal accounts as type "ach" (see payouts.api.external.yaml) —
// there is no field that distinguishes them from raw account/routing entry, so the label can't claim
// "raw" either way. Show the account's own routing/last-4 instead of guessing how it was funded.
const INTERNAL_LABELS: Record<string, string> = { ach: 'ACH', card: 'Card' };
const EXTERNAL_LABELS: Record<string, string> = {
  ach: 'ACH',
  check: 'Paper check',
  'domestic-wire': 'Domestic wire',
  'virtual-card': 'Virtual card',
};
const SIMULATION_ACTIONS = [
  { action: 'risk-approve', label: 'Risk approve', kind: 'success' },
  { action: 'risk-decline', label: 'Risk decline', kind: 'danger' },
  { action: 'collect', label: 'Collect', kind: 'primary' },
  { action: 'fail-collect', label: 'Fail collect', kind: 'danger' },
  { action: 'deliver', label: 'Deliver', kind: 'primary' },
  { action: 'fail-deliver', label: 'Fail deliver', kind: 'danger' },
];
const LIFECYCLE = [
  { key: 'scheduled', label: 'Scheduled', sub: 'Payment created' },
  { key: 'risk', label: 'Risk review', sub: 'Screened & approved' },
  { key: 'collect', label: 'Collecting funds', sub: 'Debited from funding source' },
  { key: 'deliver', label: 'In delivery', sub: 'On its way to the recipient' },
  { key: 'completed', label: 'Completed', sub: 'Delivered' },
];
type View = 'overview' | 'internal' | 'external' | 'payments' | 'new-entity' | 'entity';

// ---- Small helpers ---------------------------------------------------------
const rand = (): string => Math.random().toString(36).slice(2, 8);
const uuid = (): string => (crypto.randomUUID ? crypto.randomUUID() : rand() + rand());
const shortId = (id: string | null | undefined): string => (id && id.length > 16 ? id.slice(0, 11) + '…' + id.slice(-4) : id || '');
const nowTime = (): string => new Date().toLocaleTimeString('en-US', { hour12: false });
const money = (minor: number | undefined): string =>
  '$' + (Number(minor || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const parseMoney = (str: string): number => Math.round(parseFloat(String(str).replace(/[^0-9.]/g, '')) * 100) || 0;
function fmtDate(d?: string): string {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtAddress(a?: AddressLike): string {
  if (!a) return '—';
  const parts = [a.line1, a.line2, [a.city, a.state].filter(Boolean).join(', '), a.postalCode].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

function escapeHtml(s: string): string {
  return String(s).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);
}
function highlightJson(str: string): string {
  return escapeHtml(str)
    .replace(/&quot;([^&]+?)&quot;(\s*:)/g, '<span class="k">"$1"</span>$2')
    .replace(/: (&quot;[^&]*?&quot;)/g, ': <span class="s">$1</span>')
    .replace(/: (-?\d+\.?\d*)/g, ': <span class="n">$1</span>');
}
const AVATAR_COLORS = ['#d9e1fd', '#ffe0d6', '#ffd0ea', '#c9f8f8', '#ead6d4', '#e6ddff'];
function avatarColor(seed: string): string {
  let h = 0;
  for (const ch of seed || '') h = (h * 31 + ch.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[h];
}
function initials(name: string): string {
  return (name || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}
/** Next weekday `daysAhead` out (skips Sat/Sun), YYYY-MM-DD. */
function futureDate(daysAhead: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysAhead);
  const day = d.getDay();
  if (day === 6) d.setDate(d.getDate() + 2);
  else if (day === 0) d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}
function statusToStep(status: string): number {
  const s = (status || '').toLowerCase();
  if (s.includes('fail') || s.includes('declin') || s.includes('return') || s.includes('cancel') || s.includes('block')) return -1;
  if (s.includes('complet')) return 4;
  if (s.includes('deliver')) return 3;
  if (s.includes('collect') || s.includes('progress') || s.includes('funding')) return 2;
  if (s.includes('risk') || s.includes('approv') || s.includes('review')) return 1;
  return 0;
}
function pillForStatus(s: string): string {
  const t = (s || '').toLowerCase();
  if (t.includes('fail') || t.includes('declin') || t.includes('return') || t.includes('cancel')) return 'critical';
  if (t.includes('block')) return 'warning';
  if (t.includes('complet') || (t.includes('deliver') && t.includes('ed'))) return 'success';
  if (t.includes('deliver')) return 'informative';
  if (t.includes('collect')) return 'informative';
  if (t.includes('risk') || t.includes('review')) return 'warning';
  return 'brand';
}
/** A hidden ?env=<id> overrides key detection; otherwise the env slug is read from the key. */
function resolveEnv(key: string, fallback: EnvironmentId): EnvironmentId {
  const override = new URLSearchParams(window.location.search).get('env');
  if (override && isEnvironmentId(override)) return override;
  if (/_eilat_|eilat/i.test(key)) return 'eilat';
  if (/_staging01_|staging01/i.test(key)) return 'staging01';
  if (/_production_/i.test(key)) return 'production';
  return fallback;
}
const idtag = (s: string): ReactNode => <span className="idtag">{s}</span>;

// Generated link URL: shown as a clickable link (opens the flow in a new tab) plus a one-click
// copy button, rather than the raw URL spelled out in full alongside it.
function LinkUrlRow({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="link-url" style={{ marginTop: 12 }}>
      <a href={url} target="_blank" rel="noreferrer">
        {url}
      </a>
      <button className="btn btn-ghost btn-sm link-copy-btn" type="button" onClick={copy}>
        {copied ? <IcTick /> : <IcCopy />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

// ---- Inline icons (copied from the artifact so the console matches pixel-for-pixel) ----
const S = { fill: 'none' } as const;
const IcOverview = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <rect x="3" y="3" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
    <rect x="14" y="3" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
    <rect x="14" y="12" width="7" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
    <rect x="3" y="16" width="7" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.7" />
  </svg>
);
const IcBank = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" {...S}>
    <path
      d="M3 9l9-5 9 5M5 9v9m14-9v9M9 18v-6m6 6v-6M3 20h18"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IcWireOut = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <path d="M12 3v18M5 8l7-5 7 5M7 12h10M8 16h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcPayments = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <path d="M2 8h9M8 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M22 16h-9m3-4l-4 4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcBuilding = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <rect x="4" y="3" width="12" height="18" rx="1" stroke="currentColor" strokeWidth="1.6" />
    <path d="M8 7h1M8 11h1M8 15h1M12 7h1M12 11h1M12 15h1M16 10h4v11h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IcDisconnect = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" {...S}>
    <path
      d="M6 2H3.5A1.5 1.5 0 002 3.5v9A1.5 1.5 0 003.5 14H6M10 11l3-3-3-3M13 8H6"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IcBack = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" {...S}>
    <path d="M13 8H3M7 4L3 8l4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcPlus = ({ color = 'currentColor' }: { color?: string }) => (
  <svg width="15" height="15" viewBox="0 0 16 16" {...S}>
    <path d="M8 3v10M3 8h10" stroke={color} strokeWidth="1.7" strokeLinecap="round" />
  </svg>
);
const IcPlaid = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" {...S}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9h18" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const IcCard = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" {...S}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9.5h18" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const IcCheck = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" {...S}>
    <rect x="2.5" y="6" width="19" height="12" rx="2" stroke="currentColor" strokeWidth="1.6" />
    <path d="M6 14h5M15 14h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const IcWire = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" {...S}>
    <path d="M12 3v18M5 8l7-5 7 5M7 12h10M8 16h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcVCard = () => (
  <svg width="19" height="19" viewBox="0 0 24 24" {...S}>
    <rect x="3" y="5" width="18" height="14" rx="2.5" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3 9.5h18M7 14h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);
const IcArrowWhite = ({ size = 15, w = '1.6' }: { size?: number; w?: string }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" {...S}>
    <path d="M2 8h9M8 4l4 4-4 4" stroke="#fff" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcTick = ({ size = 13 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" {...S}>
    <path d="M3.5 8.5l3 3 6-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcCopy = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" {...S}>
    <rect x="5.5" y="5.5" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M10.5 5.5V4a1.5 1.5 0 0 0-1.5-1.5H4A1.5 1.5 0 0 0 2.5 4v5A1.5 1.5 0 0 0 4 10.5h1.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);
const IcReqTab = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" {...S}>
    <path d="M5 4h8M5 8h8M5 12h5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="2.4" cy="4" r="1" fill="currentColor" />
    <circle cx="2.4" cy="8" r="1" fill="currentColor" />
    <circle cx="2.4" cy="12" r="1" fill="currentColor" />
  </svg>
);
const IcChevron = () => (
  <svg className="chevron" width="13" height="13" viewBox="0 0 16 16" {...S}>
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcChevDown = () => (
  <svg className="es-chev" width="16" height="16" viewBox="0 0 16 16" {...S}>
    <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcRowChev = () => (
  <svg className="pay-chev" width="15" height="15" viewBox="0 0 16 16" {...S}>
    <path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcMinimize = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" {...S}>
    <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcReopen = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" {...S}>
    <path d="M10 4l-4 4 4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcLock = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" {...S}>
    <rect x="5" y="10" width="14" height="10" rx="2.5" stroke="currentColor" strokeWidth="1.7" />
    <path d="M8 10V7.5a4 4 0 018 0V10" stroke="currentColor" strokeWidth="1.7" />
    <circle cx="12" cy="15" r="1.4" fill="currentColor" />
  </svg>
);
const IcInfo = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" {...S}>
    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.4" />
    <path d="M8 7.2v3.4M8 5v.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);
const IcEmptyReq = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...S}>
    <path d="M8 6h11M8 12h11M8 18h7" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    <circle cx="4" cy="6" r="1.4" fill="currentColor" />
    <circle cx="4" cy="12" r="1.4" fill="currentColor" />
    <circle cx="4" cy="18" r="1.4" fill="currentColor" />
  </svg>
);
const IcEmptyBox = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" {...S}>
    <path d="M4 7l8-4 8 4v10l-8 4-8-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M4 7l8 4 8-4M12 11v10" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);
const IcStatIn = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <path
      d="M3 9l9-5 9 5M5 9v9m14-9v9M9 18v-6m6 6v-6M3 20h18"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const IcStatOut = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <path d="M12 3v18M5 8l7-5 7 5M7 12h10M8 16h8" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const IcStatPay = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" {...S}>
    <path
      d="M2 8h9M8 4l4 4-4 4M22 16h-9m3-4l-4 4 4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const spin: CSSProperties = { animation: 'om-spin 1s linear infinite' };
const ST_ICONS: Record<string, ReactNode> = {
  ok: (
    <svg width="16" height="16" viewBox="0 0 16 16" {...S}>
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity=".18" />
      <path d="M4.8 8.2l2 2L11 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  err: (
    <svg width="16" height="16" viewBox="0 0 16 16" {...S}>
      <circle cx="8" cy="8" r="7" fill="currentColor" opacity=".18" />
      <path d="M8 4.5V9M8 11.2v.1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  pending: (
    <svg width="16" height="16" viewBox="0 0 16 16" {...S} style={spin}>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" opacity=".25" />
      <path d="M8 2a6 6 0 016 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
};
function DeliveryIcon({ type }: { type: string }) {
  if (type === 'check') return <IcCheck />;
  if (type === 'domestic-wire') return <IcWire />;
  if (type === 'virtual-card') return <IcVCard />;
  return <IcBank size={19} />;
}

// ---- Types -----------------------------------------------------------------
type StatusKind = 'ok' | 'err' | 'pending';
type Status = { kind: StatusKind; node: ReactNode } | null;

// A non-2xx response from `api`. Carries the HTTP status so callers can tell a rejection the API
// described (4xx, with a code and message) from one it didn't (5xx), without parsing `message`.
class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}
type LogEntry = {
  id: number;
  method: string;
  path: string;
  status: number;
  ok: boolean;
  time: string;
  reqBody?: unknown;
  resBody?: unknown;
  open: boolean;
};
type AddressLike = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  aptNumber?: string;
  countryCode?: string;
};
type Entity = {
  id: string;
  type?: string;
  externalId?: string;
  metadata?: Record<string, string>;
  createdAt?: string;
  updatedAt?: string;
  name?: string;
  legalName?: string;
  businessType?: string;
  taxInfo?: { type?: string; identifierLast4?: string };
  phoneNumber?: string;
  contact?: { firstName?: string; lastName?: string };
  website?: string;
  description?: string;
  address?: AddressLike;
  legalAddress?: AddressLike;
  industry?: { naicsCode?: string; name?: string };
  owner?: { firstName?: string; lastName?: string; email?: string };
};
type Account = {
  id: string;
  ownershipType?: string;
  type?: string;
  nickname?: string;
  counterPartyName?: string;
  counterPartyEmail?: string;
  name?: string;
  isVerified?: boolean;
  externalId?: string;
  metadata?: Record<string, string>;
  bankAccount?: { accountType?: string; routingNumber?: string; accountNumberLast4?: string };
  card?: { lastFourDigits?: string; network?: string; expiration?: string };
  paperCheck?: { printName?: string; address?: AddressLike };
  domesticWire?: { routingNumber?: string; accountNumberLast4?: string; address?: AddressLike };
  virtualCard?: { counterPartyEmail?: string };
  createdAt?: string;
  updatedAt?: string;
};
const accountLast4 = (a: Account): string | undefined =>
  a.bankAccount?.accountNumberLast4 || a.card?.lastFourDigits || a.domesticWire?.accountNumberLast4;
type PaymentRow = {
  id: string;
  amount?: number;
  status?: string;
  originatingAccountId?: string;
  receivingAccountId?: string;
  deliveryPreference?: string;
  deductionDate?: string;
  deliveryDate?: string;
  externalId?: string;
  noteToRecipient?: string;
  fees?: PaymentFee[];
};
// POST /tools/fee-calculator returns an array of these (see Fee in payouts.api.external.yaml).
// Payment.fees is the same shape plus `status` (PaymentFee), always present — an empty array
// means no fee, not "unknown".
type Fee = {
  type?: string;
  chargeTo?: 'originator' | 'receiver';
  fixed?: { value?: number };
  percent?: { rate?: number; min?: number; max?: number };
  amount?: number;
};
type PaymentFee = Fee & { status?: 'charged' | 'pending' };
// Total of a fee list. An empty list means "no fee" → 0. When any fee has no resolvable amount
// (tiered / per-quote pricing) the total can't be summed → null ("varies").
const sumFees = (fees?: PaymentFee[]): number | null => {
  const list = fees || [];
  if (!list.length) return 0;
  if (list.some((fee) => typeof fee.amount !== 'number')) return null;
  return list.reduce((sum, fee) => sum + (fee.amount as number), 0);
};
// Human label for a fee total: empty → "No fee", unresolved → "Varies", else formatted money.
const feeTotalLabel = (fees?: PaymentFee[]): string => {
  const total = sumFees(fees);
  return total === null ? 'Varies' : total === 0 ? 'No fee' : money(total);
};
// Compact description of a single fee's pricing, e.g. "0.5% (max $30.00)" or "$1.50 flat".
const feePricing = (fee: Fee): string => {
  if (fee.percent && typeof fee.percent.rate === 'number') {
    const bounds = [
      typeof fee.percent.min === 'number' ? `min ${money(fee.percent.min)}` : null,
      typeof fee.percent.max === 'number' ? `max ${money(fee.percent.max)}` : null,
    ]
      .filter(Boolean)
      .join(', ');
    return `${fee.percent.rate}%${bounds ? ` (${bounds})` : ''}`;
  }
  if (fee.fixed && typeof fee.fixed.value === 'number') return `${money(fee.fixed.value)} flat`;
  return '';
};

const fv = (form: HTMLFormElement | null, name: string): string => {
  const el = form?.elements.namedItem(name) as HTMLInputElement | HTMLSelectElement | null;
  return el?.value ?? '';
};

// ============================================================================
// The console's connection state, surfaced to an embedding shell (see `onConnectionChange`)
// so it can mirror the simulator's env + partner in its own chrome. `null` = not connected.
export type SimConnection = { envLabel: string; partnerName: string };

// `embedded` renders the console inside another shell (the authenticated dashboard's
// content area) rather than as a standalone page: it drops the portal TopNav and the
// full-viewport height, filling its flex parent instead, and scopes the connect gate to
// the console region so it doesn't overlay the surrounding chrome. Everything else — the
// gate, rail, entity switcher, inspector and every API behavior — is identical.
// `onConnectionChange` reports connect/disconnect so the host can reflect the live env +
// partner (and disable its own env toggle to match) while the simulator is on screen.
export function PayoutsSimulator({
  embedded = false,
  onConnectionChange,
}: { embedded?: boolean; onConnectionChange?: (info: SimConnection | null) => void } = {}) {
  const app = useApp();
  useFlowAnalytics('simulator');

  // --- connection / session (refs so async calls read fresh values) --------
  const apiKeyRef = useRef<string>(app.apiKey || '');
  const envRef = useRef<EnvironmentId>(app.env);
  const [connected, setConnected] = useState(false);
  const [gateKey, setGateKey] = useState(app.hasKey ? app.apiKey : '');
  // By default the env is inferred from the token (a staging key → staging01). On a local
  // dev build that can't reach a local backend, so offer a checkbox to force the Local env
  // (localhost:3000) instead. Pre-checked when the portal is itself served from localhost.
  const showEnvPicker = config.environment === 'development';
  const [useLocal, setUseLocal] = useState(app.env === 'localhost');
  const [gateStatus, setGateStatus] = useState<Status>(null);
  const [envLabel, setEnvLabel] = useState(app.envLabel);
  const [partnerName, setPartnerName] = useState(app.partnerName || 'connected');
  const [riskState, setRiskState] = useState<'idle' | 'pending' | 'ok' | 'err'>('idle');

  // --- entity context ------------------------------------------------------
  const [entities, setEntities] = useState<Entity[]>([]);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [entityName, setEntityName] = useState<string>('');
  const [entityMenuOpen, setEntityMenuOpen] = useState(false);
  const entitySwitchRef = useRef<HTMLDivElement>(null);

  // --- entity details (view / edit) ----------------------------------------
  const [entityDetail, setEntityDetail] = useState<Entity | null>(null);
  const [entityDetailLoading, setEntityDetailLoading] = useState(false);
  const [entityEditMode, setEntityEditMode] = useState(false);
  const [entityUpdateStatus, setEntityUpdateStatus] = useState<Status>(null);
  const entityEditForm = useRef<HTMLFormElement>(null);

  // --- entity-scoped data --------------------------------------------------
  const [internal, setInternal] = useState<Account[]>([]);
  const [external, setExternal] = useState<Account[]>([]);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [paymentsHasMore, setPaymentsHasMore] = useState(false);
  const [paymentsLoadingMore, setPaymentsLoadingMore] = useState(false);
  // Loading flags so each data surface shows a spinner rather than an empty
  // state while its request is still in flight (initial connect + entity switch).
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [paymentsLoading, setPaymentsLoading] = useState(false);

  // --- navigation ----------------------------------------------------------
  const [view, setView] = useState<View>('overview');
  const [acctMode, setAcctMode] = useState<'list' | 'add' | 'detail'>('list');
  const [payMode, setPayMode] = useState<'list' | 'create' | 'detail'>('list');
  const mainRef = useRef<HTMLDivElement>(null);

  // --- account drill-down (GET /accounts/{id}) + delete --------------------
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [accountDetail, setAccountDetail] = useState<Account | null>(null);
  const [accountDetailLoading, setAccountDetailLoading] = useState(false);
  const [accountActionStatus, setAccountActionStatus] = useState<Status>(null);

  // --- accounts add forms --------------------------------------------------
  const [newFundType, setNewFundType] = useState('plaid');
  const [newDelivType, setNewDelivType] = useState('check');
  const [fundStatus, setFundStatus] = useState<Status>(null);
  const [delivStatus, setDelivStatus] = useState<Status>(null);
  const [linkUrl, setLinkUrl] = useState<string | null>(null);
  const [showPollBtn, setShowPollBtn] = useState(false);
  const preLinkInternalIds = useRef<Set<string>>(new Set());
  const externalForm = useRef<HTMLFormElement>(null);

  // --- accounts: re-verify (Plaid micro-deposit) ---------------------------
  const [verifyingAccountId, setVerifyingAccountId] = useState<string | null>(null);
  const [verifyLinkUrl, setVerifyLinkUrl] = useState<string | null>(null);
  const [verifyStatus, setVerifyStatus] = useState<Status>(null);

  // --- payment -------------------------------------------------------------
  const [currentPaymentId, setCurrentPaymentId] = useState<string | null>(null);
  const [payStatus, setPayStatus] = useState<Status>(null);
  const [actionStatus, setActionStatus] = useState<Status>(null);
  const [pay, setPay] = useState({
    originating: '',
    receiving: '',
    deductionDate: '',
    preference: '',
    amount: '1,250.00',
    externalId: 'invoice-2043',
    note: 'July materials order',
    mcc: '5045',
  });
  const [feeLoading, setFeeLoading] = useState(false);
  const [feeAmount, setFeeAmount] = useState<number | null>(null);
  const [feeError, setFeeError] = useState<string | null>(null);
  // Fast-payment eligibility per delivery preference (POST /tools/fast-payment-eligibility).
  // Maps a fast deliveryPreference → whether it's eligible for the prospective payment.
  const [fastElig, setFastElig] = useState<Record<string, boolean>>({});
  const [fastEligLoading, setFastEligLoading] = useState(false);
  // True when the eligibility engine couldn't be reached — per the spec, fall back to standard
  // speed only (treat every fast variant as ineligible).
  const [fastEligError, setFastEligError] = useState(false);
  // Estimated delivery date per delivery preference (POST /tools/delivery-eta).
  const [etaByPref, setEtaByPref] = useState<Record<string, string>>({});
  // Cancel a scheduled payment (POST /payments/{id}/cancel).
  const [cancelStatus, setCancelStatus] = useState<Status>(null);

  // --- new-entity ----------------------------------------------------------
  const [bizStatus, setBizStatus] = useState<Status>(null);
  const [showCheckLim, setShowCheckLim] = useState(false);
  const pendingEntityId = useRef<string | null>(null);
  const entityForm = useRef<HTMLFormElement>(null);

  // --- inspector -----------------------------------------------------------
  const [reqLog, setReqLog] = useState<LogEntry[]>([]);
  const [inspOpen, setInspOpen] = useState(true);
  const logSeq = useRef(0);

  // ---- request logging ----------------------------------------------------
  const logCall = useCallback((method: string, path: string, status: number, reqBody?: unknown, resBody?: unknown) => {
    setReqLog((prev) => [
      { id: logSeq.current++, method, path, status, ok: status >= 200 && status < 300, time: nowTime(), reqBody, resBody, open: false },
      ...prev,
    ]);
  }, []);

  // ---- risk session -------------------------------------------------------
  const getRisk = useCallback(async (): Promise<string | undefined> => {
    setRiskState((s) => (s === 'ok' ? 'ok' : 'pending'));
    const token = await getSonarSessionToken(envRef.current, apiKeyRef.current, entityId ?? undefined);
    setRiskState(token ? 'ok' : 'err');
    return token;
  }, [entityId]);

  // ---- core API call ------------------------------------------------------
  const api = useCallback(
    async (
      method: string,
      path: string,
      body?: unknown,
      opts: { entityId?: string; bearer?: string; silent?: boolean } = {}
    ): Promise<any> => {
      const url = apiBaseUrl(envRef.current).replace(/\/$/, '') + path;
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (opts.bearer) headers['Authorization'] = 'Bearer ' + opts.bearer;
      else headers['api-key'] = apiKeyRef.current;
      if (opts.entityId) headers['Melio-Entity-Id'] = opts.entityId;
      if (method === 'POST') headers['Idempotency-Key'] = uuid();
      if (method === 'POST' && !opts.bearer && !path.endsWith('/accounts/link')) {
        const sonarToken = await getRisk();
        if (sonarToken) headers['Melio-Sonar-Token'] = sonarToken;
      }
      let res: Response, data: any;
      try {
        res = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
        const text = await res.text();
        try {
          data = text ? JSON.parse(text) : undefined;
        } catch {
          data = text;
        }
      } catch (e) {
        if (!opts.silent) logCall(method, path, 0, body, String(e));
        throw new Error('Network error: ' + (e as Error).message);
      }
      if (!opts.silent) logCall(method, path, res.status, body, data);
      if (!res.ok) {
        const msg = data && data.error ? `${data.error.code}: ${data.error.message}` : `HTTP ${res.status}`;
        throw new ApiError(msg, res.status, data?.error?.code);
      }
      return data;
    },
    [getRisk, logCall]
  );

  // ---- data loaders -------------------------------------------------------
  const loadAccounts = useCallback(
    async (eid: string) => {
      setAccountsLoading(true);
      try {
        const [inRes, exRes] = await Promise.all([
          api('GET', '/accounts?ownershipType=internal', undefined, { entityId: eid }),
          api('GET', '/accounts?ownershipType=external', undefined, { entityId: eid }),
        ]);
        setInternal((inRes && inRes.data) || []);
        setExternal((exRes && exRes.data) || []);
      } catch {
        setInternal([]);
        setExternal([]);
      } finally {
        setAccountsLoading(false);
      }
    },
    [api]
  );

  const loadPayments = useCallback(
    async (eid: string) => {
      setPaymentsLoading(true);
      try {
        const res = await api('GET', '/payments', undefined, { entityId: eid });
        const items = (res && (res.data || res)) || [];
        setPayments(Array.isArray(items) ? items : []);
        setPaymentsHasMore(Boolean(res && res.hasMore));
      } catch {
        setPayments([]);
        setPaymentsHasMore(false);
      } finally {
        setPaymentsLoading(false);
      }
    },
    [api]
  );

  const loadMorePayments = useCallback(async () => {
    if (!entityId || !payments.length) return;
    setPaymentsLoadingMore(true);
    try {
      const cursor = payments[payments.length - 1].id;
      const res = await api('GET', '/payments?startingAfter=' + encodeURIComponent(cursor), undefined, {
        entityId,
      });
      const items = (res && (res.data || res)) || [];
      setPayments((prev) => [...prev, ...(Array.isArray(items) ? items : [])]);
      setPaymentsHasMore(Boolean(res && res.hasMore));
    } catch {
      /* keep the page already loaded */
    } finally {
      setPaymentsLoadingMore(false);
    }
  }, [api, entityId, payments]);

  const loadEntityData = useCallback(
    async (eid: string) => {
      await Promise.all([loadAccounts(eid), loadPayments(eid)]);
    },
    [loadAccounts, loadPayments]
  );

  const nameOf = (e: Entity): string => e.name || e.legalName || e.id;

  const selectEntity = useCallback(
    async (id: string, opts: { silent?: boolean } = {}) => {
      const ent = entities.find((e) => e.id === id);
      if (!ent) return;
      setEntityId(ent.id);
      setEntityName(nameOf(ent));
      setCurrentPaymentId(null);
      setEntityMenuOpen(false);
      await loadEntityData(ent.id);
      if (!opts.silent) setView((v) => (v === 'new-entity' ? 'overview' : v));
    },
    [entities, loadEntityData]
  );

  const loadEntities = useCallback(async () => {
    setEntitiesLoading(true);
    let list: Entity[] = [];
    try {
      const res = await api('GET', '/entities');
      list = (res && (res.data || res)) || [];
      if (!Array.isArray(list)) list = [];
    } catch {
      list = [];
    } finally {
      setEntitiesLoading(false);
    }
    setEntities(list);
    return list;
  }, [api]);

  const loadEntityDetail = useCallback(
    async (eid: string) => {
      setEntityDetailLoading(true);
      try {
        const detail = await api('GET', '/entities/' + eid);
        setEntityDetail(detail);
      } catch {
        setEntityDetail(null);
      } finally {
        setEntityDetailLoading(false);
      }
    },
    [api]
  );

  const openEntityEdit = () => {
    setEntityUpdateStatus(null);
    setEntityEditMode(true);
  };

  // UpdateEntityRequest — only name/phoneNumber/businessType/industry/contact/website/
  // description/address/externalId/metadata/owner are patchable; legalName, legalAddress
  // and taxInfo are fixed at creation (payouts.api.external.yaml) and aren't in this form.
  const buildEntityUpdateBody = (): Record<string, unknown> => {
    const f = entityEditForm.current;
    const body: Record<string, unknown> = {};
    const name = fv(f, 'name');
    if (name) body.name = name;
    const phoneNumber = fv(f, 'phoneNumber');
    if (phoneNumber) body.phoneNumber = phoneNumber;
    const businessType = fv(f, 'businessType');
    if (businessType) body.businessType = businessType;
    const naicsCode = fv(f, 'naicsCode');
    const industryName = fv(f, 'industryName');
    if (naicsCode || industryName) body.industry = { naicsCode, ...(industryName ? { name: industryName } : {}) };
    const contactFirst = fv(f, 'contactFirst');
    const contactLast = fv(f, 'contactLast');
    if (contactFirst || contactLast) body.contact = { firstName: contactFirst, lastName: contactLast };
    const website = fv(f, 'website');
    if (website) body.website = website;
    const description = fv(f, 'description');
    if (description) body.description = description;
    const line1 = fv(f, 'line1');
    if (line1) {
      const line2 = fv(f, 'line2');
      const aptNumber = fv(f, 'aptNumber');
      body.address = {
        line1,
        city: fv(f, 'city'),
        state: fv(f, 'state'),
        postalCode: fv(f, 'postalCode'),
        ...(line2 ? { line2 } : {}),
        ...(aptNumber ? { aptNumber } : {}),
      };
    }
    const externalId = fv(f, 'externalId');
    if (externalId) body.externalId = externalId;
    const ownerFirst = fv(f, 'ownerFirst');
    const ownerLast = fv(f, 'ownerLast');
    const ownerEmail = fv(f, 'ownerEmail');
    const ownerDob = fv(f, 'ownerDob');
    const ownerPhone = fv(f, 'ownerPhone');
    if (ownerFirst || ownerLast || ownerEmail || ownerDob || ownerPhone)
      body.owner = {
        ...(ownerFirst ? { firstName: ownerFirst } : {}),
        ...(ownerLast ? { lastName: ownerLast } : {}),
        ...(ownerEmail ? { email: ownerEmail } : {}),
        ...(ownerDob ? { dateOfBirth: ownerDob } : {}),
        ...(ownerPhone ? { phoneNumber: ownerPhone } : {}),
      };
    return body;
  };

  const saveEntityEdit = useCallback(async () => {
    if (!entityId) return;
    setEntityUpdateStatus({ kind: 'pending', node: 'Saving changes…' });
    try {
      const updated = await api('PATCH', '/entities/' + entityId, buildEntityUpdateBody());
      setEntityDetail(updated);
      setEntityName(nameOf(updated));
      setEntities((prev) => prev.map((e) => (e.id === entityId ? { ...e, ...updated } : e)));
      setEntityUpdateStatus({ kind: 'ok', node: 'Business updated.' });
      setEntityEditMode(false);
    } catch (e) {
      setEntityUpdateStatus({ kind: 'err', node: (e as Error).message });
    }
    // buildEntityUpdateBody reads live form refs; not memoized on purpose.
  }, [api, entityId]);

  // ---- connect / disconnect ----------------------------------------------
  const connect = useCallback(async () => {
    const key = gateKey.trim();
    if (!key) {
      setGateStatus({ kind: 'err', node: 'Paste your partner API key.' });
      return;
    }
    const resolved = showEnvPicker && useLocal ? 'localhost' : resolveEnv(key, app.env);
    envRef.current = resolved;
    apiKeyRef.current = key;
    setEnvLabel(ENVIRONMENTS[resolved].label);
    setGateStatus({ kind: 'pending', node: `Connecting to ${ENVIRONMENTS[resolved].label}…` });
    try {
      const health = await api('GET', '/health');
      setPartnerName(health?.partnerName || 'partner');
      app.setApiKey(key);
      app.setEnv(resolved);
      setConnected(true);
      trackEvent('simulator', 'Connected', { targetEnvironment: resolved });
      setGateStatus(null);
      getRisk();
      const list = await loadEntities();
      if (list.length) {
        const first = list[0];
        setEntityId(first.id);
        setEntityName(nameOf(first));
        await loadEntityData(first.id);
      }
      setView('overview');
    } catch (e) {
      trackEvent('simulator', 'Connection Failed', { targetEnvironment: resolved, reason: (e as Error).message });
      setGateStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [gateKey, app, api, getRisk, loadEntities, loadEntityData, showEnvPicker, useLocal]);

  const disconnect = useCallback(() => {
    setVerifyingAccountId(null);
    setVerifyLinkUrl(null);
    setVerifyStatus(null);
    setConnected(false);
    setView('overview');
    setEntities([]);
    setEntityId(null);
    setEntityName('');
    setInternal([]);
    setExternal([]);
    setPayments([]);
    setPaymentsHasMore(false);
    setPaymentsLoadingMore(false);
    setCurrentPaymentId(null);
    setReqLog([]);
    setGateStatus(null);
    setEntitiesLoading(false);
    setAccountsLoading(false);
    setPaymentsLoading(false);
    setEntityDetail(null);
    setEntityDetailLoading(false);
    setEntityEditMode(false);
    setEntityUpdateStatus(null);
    setAcctMode('list');
    setSelectedAccountId(null);
    setAccountDetail(null);
    setAccountActionStatus(null);
  }, []);

  // Mirror connect/disconnect up to an embedding host (the dashboard) so it can show the live
  // env + partner in its own chrome. Reports null while disconnected, and once more on unmount
  // (leaving the section) so the host never holds a stale "connected" state.
  useEffect(() => {
    onConnectionChange?.(connected ? { envLabel, partnerName } : null);
  }, [connected, envLabel, partnerName, onConnectionChange]);
  useEffect(() => () => onConnectionChange?.(null), [onConnectionChange]);

  const envHint = ((): ReactNode => {
    const key = gateKey.trim();
    const forceLocal = showEnvPicker && useLocal;
    if (!key && !forceLocal) return null;
    const target: EnvironmentId = forceLocal ? 'localhost' : resolveEnv(key, app.env);
    return (
      <>
        {forceLocal ? 'Using' : 'Detected'} environment: <code>{ENVIRONMENTS[target].label}</code> · {apiBaseUrl(target)}
      </>
    );
  })();

  // ---- navigation ---------------------------------------------------------
  const showView = useCallback(
    (name: View) => {
      if ((name === 'internal' || name === 'external' || name === 'payments' || name === 'entity') && !entityId) return;
      trackEvent('simulator', 'View Viewed', { view: name });
      setView(name);
      if (name === 'internal' || name === 'external') setAcctMode('list');
      if (name === 'payments') setPayMode('list');
      if (name === 'new-entity') {
        setBizStatus(null);
        setShowCheckLim(false);
      }
      if (name === 'entity') {
        setEntityEditMode(false);
        setEntityUpdateStatus(null);
      }
      mainRef.current?.scrollTo(0, 0);
    },
    [entityId]
  );

  // ---- accounts: create internal -----------------------------------------
  const openAddAccount = () => {
    if (view === 'internal') {
      setFundStatus(null);
      setNewFundType('plaid');
      setShowPollBtn(false);
      setLinkUrl(null);
    } else {
      setDelivStatus(null);
      setNewDelivType('check');
    }
    setAcctMode('add');
  };

  // Manual fallback for when the iframe's postMessage doesn't arrive (e.g. an embedder that
  // swallows it) — a single on-demand check, not a poll.
  const pollInternalOnce = useCallback(async () => {
    if (!entityId) return;
    try {
      const res = await api('GET', '/accounts?ownershipType=internal', undefined, { silent: true, entityId });
      const accts: Account[] = res.data || [];
      const seen = preLinkInternalIds.current;
      const fresh = accts.filter((a) => !seen.has(a.id));
      const acct = fresh.length ? fresh[0] : undefined;
      if (acct) {
        setFundStatus({ kind: 'ok', node: <>Internal account added: {idtag(acct.id)}</> });
        await loadAccounts(entityId);
        window.setTimeout(() => setAcctMode('list'), 650);
      } else {
        setFundStatus({ kind: 'err', node: 'No new account found yet — finish the flow in the frame above and try again.' });
      }
    } catch (e) {
      setFundStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [api, entityId, loadAccounts]);

  const generateLink = useCallback(async () => {
    if (!entityId) return;
    const type = newFundType;
    setFundStatus({ kind: 'pending', node: 'Generating link…' });
    try {
      const existing = await api('GET', '/accounts?ownershipType=internal', undefined, { silent: true, entityId });
      preLinkInternalIds.current = new Set((existing.data || []).map((a: Account) => a.id));
      const res = await api('POST', '/accounts/link', { type }, { entityId });
      setLinkUrl(res.data.url);
      setShowPollBtn(true);
      setFundStatus({ kind: 'pending', node: 'Complete the flow in the frame below — waiting for it to finish…' });
    } catch (e) {
      setFundStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [api, entityId, newFundType]);

  const startVerifyAccount = useCallback(
    async (accountId: string) => {
      if (!entityId) return;
      setVerifyingAccountId(accountId);
      setVerifyLinkUrl(null);
      setVerifyStatus({ kind: 'pending', node: 'Generating verification link…' });
      try {
        const res = await api('POST', '/accounts/' + accountId + '/verify/link', undefined, { entityId });
        setVerifyLinkUrl(res.data.url);
        setVerifyStatus({ kind: 'pending', node: 'Complete micro-deposit verification in the frame below — waiting for it to finish…' });
      } catch (e) {
        setVerifyStatus({ kind: 'err', node: (e as Error).message });
      }
    },
    [api, entityId]
  );

  // Primary completion signal for both the add-account and verify-account iframes: the hosted
  // pages postMessage `{ type: 'melio:accounts:internal', flow, kind, status, accountId?, message? }`
  // back to the parent window on success/error/cancel (see lib/embedMessages.ts) — no polling needed.
  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data as {
        type?: string;
        flow?: string;
        kind?: string;
        status?: string;
        accountId?: string;
        message?: string;
      } | null;
      if (!data || data.type !== 'melio:accounts:internal') return;
      if (data.flow === 'add') {
        if (data.status === 'success' && data.accountId) {
          setFundStatus({ kind: 'ok', node: <>Internal account added: {idtag(data.accountId)}</> });
          if (entityId) {
            loadAccounts(entityId);
            window.setTimeout(() => setAcctMode('list'), 650);
          }
        } else if (data.status === 'error') {
          setFundStatus({ kind: 'err', node: data.message || 'Linking failed.' });
        } else if (data.status === 'cancelled') {
          setFundStatus({ kind: 'err', node: 'Link cancelled.' });
        }
      } else if (data.flow === 'verify') {
        if (data.status === 'success') {
          setVerifyStatus({ kind: 'ok', node: 'Account verified.' });
          if (entityId) loadAccounts(entityId);
          window.setTimeout(() => {
            setVerifyingAccountId(null);
            setVerifyLinkUrl(null);
          }, 1200);
        } else if (data.status === 'error') {
          setVerifyStatus({ kind: 'err', node: data.message || 'Verification failed.' });
        } else if (data.status === 'cancelled') {
          setVerifyStatus({ kind: 'err', node: 'Verification cancelled.' });
        }
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [entityId, loadAccounts]);

  const selectFundingType = (t: string) => {
    setNewFundType(t);
    setShowPollBtn(false);
    setLinkUrl(null);
    setFundStatus(null);
  };

  // ---- accounts: create external ------------------------------------------
  const buildExternalBody = (): Record<string, unknown> => {
    const f = externalForm.current;
    const type = newDelivType;
    const counterPartyName = fv(f, 'counterPartyName');
    const counterPartyEmail = fv(f, 'counterPartyEmail');
    if (type === 'check')
      return {
        ownershipType: 'external',
        type: 'check',
        counterPartyName,
        counterPartyEmail,
        paperCheck: {
          address: {
            line1: fv(f, 'checkLine1'),
            ...(fv(f, 'checkLine2') ? { line2: fv(f, 'checkLine2') } : {}),
            city: fv(f, 'checkCity'),
            state: fv(f, 'checkState'),
            postalCode: fv(f, 'checkPostal'),
            ...(fv(f, 'checkApt') ? { aptNumber: fv(f, 'checkApt') } : {}),
          },
        },
      };
    if (type === 'domestic-wire')
      return {
        ownershipType: 'external',
        type: 'domestic-wire',
        counterPartyName,
        counterPartyEmail,
        bankAccount: { accountNumber: fv(f, 'wireAccountNumber'), routingNumber: fv(f, 'wireRoutingNumber') },
        address: { line1: fv(f, 'wireLine1'), city: fv(f, 'wireCity'), state: fv(f, 'wireState'), postalCode: fv(f, 'wirePostal') },
      };
    return { ownershipType: 'external', type: 'virtual-card', counterPartyName, counterPartyEmail };
  };

  const createExternal = useCallback(async () => {
    if (!entityId) return;
    setDelivStatus({ kind: 'pending', node: 'Creating external account…' });
    try {
      const acct = await api('POST', '/accounts', buildExternalBody(), { entityId });
      setDelivStatus({ kind: 'ok', node: <>External account created: {idtag(acct.id)}</> });
      await loadAccounts(entityId);
      window.setTimeout(() => setAcctMode('list'), 650);
    } catch (e) {
      setDelivStatus({ kind: 'err', node: (e as Error).message });
    }
    // buildExternalBody reads live form refs + newDelivType; not memoized on purpose.
  }, [api, entityId, loadAccounts, newDelivType]);

  // ---- accounts: drill down + delete --------------------------------------
  const openAccountDetail = useCallback(
    async (id: string) => {
      setSelectedAccountId(id);
      setAccountDetail(null);
      setAccountActionStatus(null);
      setAcctMode('detail');
      mainRef.current?.scrollTo(0, 0);
      if (!entityId) return;
      setAccountDetailLoading(true);
      try {
        const acct = await api('GET', '/accounts/' + id, undefined, { entityId });
        setAccountDetail(acct);
      } catch {
        setAccountDetail(null);
      } finally {
        setAccountDetailLoading(false);
      }
    },
    [api, entityId]
  );

  const deleteAccount = useCallback(
    async (id: string) => {
      if (!entityId) return;
      if (!window.confirm('Delete this account? This cannot be undone.')) return;
      setAccountActionStatus({ kind: 'pending', node: 'Deleting account…' });
      try {
        // DELETE /accounts/{id} → 204. 409 ACCOUNT_IN_USE when a payment references it;
        // 403 BUSINESS_NOT_ELIGIBLE when the business can't modify its accounts.
        await api('DELETE', '/accounts/' + id, undefined, { entityId });
        setAccountActionStatus({ kind: 'ok', node: 'Account deleted.' });
        await loadAccounts(entityId);
        setAcctMode('list');
        setSelectedAccountId(null);
        setAccountDetail(null);
      } catch (e) {
        setAccountActionStatus({ kind: 'err', node: (e as Error).message });
      }
    },
    [api, entityId, loadAccounts]
  );

  // ---- payment ------------------------------------------------------------
  const originatingType = internal.find((a) => a.id === pay.originating)?.type || 'ach';
  const receivingType = external.find((a) => a.id === pay.receiving)?.type || 'ach';
  const payPrefs = PREFERENCES[receivingType] || ['standard-ach'];
  // The first entry of each rail is its normal-speed value; the rest are faster variants gated by
  // the fast-payment-eligibility check.
  const standardPref = payPrefs[0];
  const isFastPref = (p: string): boolean => p !== standardPref;
  // A fast variant is unavailable when the engine says so (eligible=false) or couldn't be reached.
  const fastPrefIneligible = (p: string): boolean => isFastPref(p) && (fastEligError || fastElig[p] === false);
  const chosenPref = payPrefs.includes(pay.preference) ? pay.preference : standardPref;
  // Never submit an ineligible fast variant — fall back to the rail's standard speed.
  const effPref = fastPrefIneligible(chosenPref) ? standardPref : chosenPref;

  // Live fee preview via POST /tools/fee-calculator, debounced against amount/account/speed changes.
  useEffect(() => {
    if (payMode !== 'create' || !entityId || !pay.originating || !pay.receiving) {
      setFeeAmount(null);
      setFeeError(null);
      setFeeLoading(false);
      return;
    }
    const amountMinor = parseMoney(pay.amount);
    if (!amountMinor) {
      setFeeAmount(null);
      setFeeError(null);
      setFeeLoading(false);
      return;
    }
    let cancelled = false;
    setFeeLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await api(
          'POST',
          '/tools/fee-calculator',
          {
            originatingAccountId: pay.originating,
            receivingAccountId: pay.receiving,
            deliveryPreference: effPref,
            amount: amountMinor,
          },
          { silent: true, entityId }
        );
        if (cancelled) return;
        setFeeAmount(sumFees(Array.isArray(res) ? res : []));
        setFeeError(null);
      } catch (e) {
        if (cancelled) return;
        setFeeAmount(null);
        setFeeError((e as Error).message);
      } finally {
        if (!cancelled) setFeeLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [api, payMode, entityId, pay.originating, pay.receiving, pay.amount, effPref]);

  // Fast-payment eligibility via POST /tools/fast-payment-eligibility — which faster-than-standard
  // delivery options are available for these accounts + amount. Drives the disabled state of the
  // fast entries in the Delivery speed picker. Debounced against the same inputs as the fee preview.
  useEffect(() => {
    if (payMode !== 'create' || !entityId || !pay.originating || !pay.receiving) {
      setFastElig({});
      setFastEligError(false);
      setFastEligLoading(false);
      return;
    }
    const amountMinor = parseMoney(pay.amount);
    if (!amountMinor) {
      setFastElig({});
      setFastEligError(false);
      setFastEligLoading(false);
      return;
    }
    let cancelled = false;
    setFastEligLoading(true);
    const t = window.setTimeout(async () => {
      try {
        const res = await api(
          'POST',
          '/tools/fast-payment-eligibility',
          { originatingAccountId: pay.originating, receivingAccountId: pay.receiving, amount: amountMinor },
          { silent: true, entityId }
        );
        if (cancelled) return;
        const map: Record<string, boolean> = {};
        for (const d of (res?.eligibilities as { deliveryPreference?: string; eligible?: boolean }[]) || [])
          if (d.deliveryPreference) map[d.deliveryPreference] = !!d.eligible;
        setFastElig(map);
        setFastEligError(false);
      } catch {
        if (cancelled) return;
        // Engine unavailable → standard speed only (spec: treat fast variants as ineligible).
        setFastElig({});
        setFastEligError(true);
      } finally {
        if (!cancelled) setFastEligLoading(false);
      }
    }, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [api, payMode, entityId, pay.originating, pay.receiving, pay.amount]);

  // Delivery-date estimates via POST /tools/delivery-eta — one per delivery preference for the
  // receiving rail. Drives the "Estimated delivery" hint for the chosen speed.
  useEffect(() => {
    if (payMode !== 'create' || !entityId || !pay.originating || !pay.receiving) {
      setEtaByPref({});
      return;
    }
    const amountMinor = parseMoney(pay.amount);
    if (!amountMinor) {
      setEtaByPref({});
      return;
    }
    let cancelled = false;
    const t = window.setTimeout(async () => {
      try {
        const res = await api(
          'POST',
          '/tools/delivery-eta',
          {
            originatingAccountId: pay.originating,
            receivingAccountId: pay.receiving,
            amount: amountMinor,
            ...(pay.deductionDate ? { deductionDate: pay.deductionDate } : {}),
          },
          { silent: true, entityId }
        );
        if (cancelled) return;
        const map: Record<string, string> = {};
        for (const e of (res?.estimates as { deliveryPreference?: string; deliveryDate?: string }[]) || [])
          if (e.deliveryPreference && e.deliveryDate) map[e.deliveryPreference] = e.deliveryDate;
        setEtaByPref(map);
      } catch {
        if (!cancelled) setEtaByPref({});
      }
    }, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [api, payMode, entityId, pay.originating, pay.receiving, pay.amount, pay.deductionDate]);

  const newPayment = useCallback(() => {
    if (!internal.length || !external.length) {
      const needInternal = !internal.length;
      setView(needInternal ? 'internal' : 'external');
      setAcctMode('list');
      const msg = 'Add at least one internal account and one external account before creating a payment.';
      if (needInternal) setFundStatus({ kind: 'pending', node: msg });
      else setDelivStatus({ kind: 'pending', node: msg });
      return;
    }
    setPay((p) => ({
      ...p,
      originating: internal.some((a) => a.id === p.originating) ? p.originating : internal[0].id,
      receiving: external.some((a) => a.id === p.receiving) ? p.receiving : external[0].id,
      deductionDate: p.deductionDate || futureDate(4),
    }));
    setView('payments');
    setPayMode('create');
    setPayStatus(null);
  }, [internal, external]);

  const openPayment = useCallback(
    (id: string) => {
      setCurrentPaymentId(id);
      setView('payments');
      setPayMode('detail');
      setCancelStatus(null);
      mainRef.current?.scrollTo(0, 0);
      // Pull the freshest status.
      if (entityId)
        api('GET', '/payments/' + id, undefined, { entityId })
          .then((p) => {
            if (p && p.status)
              setPayments((prev) => prev.map((x) => (x.id === id ? { ...x, status: p.status, fees: p.fees ?? x.fees } : x)));
          })
          .catch(() => {});
    },
    [api, entityId]
  );

  const createPayment = useCallback(async () => {
    if (!entityId || !pay.originating || !pay.receiving) return;
    const body = {
      amount: parseMoney(pay.amount),
      originatingAccountId: pay.originating,
      receivingAccountId: pay.receiving,
      deductionDate: pay.deductionDate,
      deliveryPreference: effPref,
      externalId: pay.externalId + '-' + rand(),
      noteToRecipient: pay.note,
      compliance: {
        type: 'goods-and-services',
        goodsReceived: true,
        ...(originatingType === 'card' ? { merchantCategoryCode: pay.mcc } : {}),
      },
    };
    setPayStatus({ kind: 'pending', node: 'Creating payment…' });
    try {
      const payment = await api('POST', '/payments', body, { entityId });
      trackEvent('simulator', 'Payment Created', { deliveryPreference: effPref, originatingType });
      setPayStatus({ kind: 'ok', node: <>Payment created: {idtag(payment.id)}</> });
      await loadPayments(entityId);
      window.setTimeout(() => openPayment(payment.id), 600);
    } catch (e) {
      trackEvent('simulator', 'Payment Creation Failed', { deliveryPreference: effPref, reason: (e as Error).message });
      setPayStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [api, entityId, pay, effPref, originatingType, loadPayments, openPayment]);

  const refreshPaymentStatus = useCallback(async () => {
    if (!currentPaymentId || !entityId) return;
    try {
      const p = await api('GET', '/payments/' + currentPaymentId, undefined, { entityId });
      if (p && p.status)
        setPayments((prev) => prev.map((x) => (x.id === currentPaymentId ? { ...x, status: p.status, fees: p.fees ?? x.fees } : x)));
    } catch {
      /* keep last known status */
    }
  }, [api, currentPaymentId, entityId]);

  const cancelPayment = useCallback(async () => {
    if (!currentPaymentId || !entityId) return;
    if (!window.confirm('Cancel this payment? It will move to canceled.')) return;
    setCancelStatus({ kind: 'pending', node: 'Canceling payment…' });
    try {
      // POST /payments/{id}/cancel → the canceled Payment. 409 PAYMENT_NOT_CANCELABLE once processing began.
      const p = await api('POST', '/payments/' + currentPaymentId + '/cancel', undefined, { entityId });
      setCancelStatus({ kind: 'ok', node: 'Payment canceled.' });
      setPayments((prev) =>
        prev.map((x) => (x.id === currentPaymentId ? { ...x, status: p?.status || 'canceled', fees: p?.fees ?? x.fees } : x))
      );
    } catch (e) {
      setCancelStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [api, currentPaymentId, entityId]);

  const runSimulationAction = useCallback(
    async (action: string) => {
      if (!currentPaymentId) return;
      const status = payments.find((x) => x.id === currentPaymentId)?.status || 'scheduled';

      // Out-of-order transitions come back as a 500, so catch the ones the status makes certain
      // (delivering a payment that was never collected, acting on a finished one) before sending.
      const blocked = blockedReason(action, status);
      if (blocked) {
        setActionStatus({ kind: 'err', node: blocked });
        trackEvent('simulator', 'Payment Action Blocked', { action, status });
        return;
      }

      setActionStatus({ kind: 'pending', node: `Applying ${action}…` });
      try {
        await api('POST', '/simulations/payments/' + currentPaymentId + '/actions', { action });
        trackEvent('simulator', 'Payment Action Applied', { action });
        setActionStatus({ kind: 'ok', node: `Applied ${action}.` });
      } catch (e) {
        // A 4xx names its own reason; a 5xx doesn't, so add the likeliest cause and keep the raw
        // message so the actual response is still visible.
        const err = e as ApiError;
        setActionStatus({
          kind: 'err',
          node:
            err.status >= 500
              ? `Couldn't apply "${actionLabel(action)}" — the API returned ${err.status}. ${serverErrorHelp(action, status)}`
              : err.message,
        });
      }
      await refreshPaymentStatus();
    },
    [api, currentPaymentId, payments, refreshPaymentStatus]
  );

  // ---- new entity ---------------------------------------------------------
  const createEntity = useCallback(async () => {
    const f = entityForm.current;
    const address = { line1: fv(f, 'line1'), city: fv(f, 'city'), state: fv(f, 'state'), postalCode: fv(f, 'postalCode') };
    const body = {
      type: 'business',
      externalId: 'partner-biz-' + rand(),
      name: fv(f, 'name'),
      legalName: fv(f, 'legalName'),
      phoneNumber: fv(f, 'phoneNumber'),
      businessType: fv(f, 'businessType'),
      taxInfo: { type: fv(f, 'taxType'), identifier: fv(f, 'taxIdentifier') },
      industry: { naicsCode: fv(f, 'naicsCode') },
      contact: { firstName: fv(f, 'contactFirst'), lastName: fv(f, 'contactLast') },
      website: fv(f, 'website'),
      address,
      legalAddress: address,
      owner: {
        firstName: fv(f, 'ownerFirst'),
        lastName: fv(f, 'ownerLast'),
        email: fv(f, 'ownerEmail'),
        dateOfBirth: fv(f, 'ownerDob'),
        phoneNumber: fv(f, 'ownerPhone'),
      },
    };
    setBizStatus({ kind: 'pending', node: 'Creating business entity…' });
    try {
      const entity = await api('POST', '/entities', body);
      trackEvent('simulator', 'Business Created', { businessType: body.businessType });
      setBizStatus({ kind: 'ok', node: <>Business created: {idtag(entity.id)}</> });
      setShowCheckLim(true);
      pendingEntityId.current = entity.id;
      setEntityId(entity.id);
      setEntityName(nameOf(entity));
      // Refresh the entity switcher and the new entity's resource counts in parallel — don't
      // gate the counter refetch on finding the new entity in the (possibly stale) list.
      await Promise.all([loadEntities(), loadEntityData(entity.id)]);
      window.setTimeout(() => setView('overview'), 700);
    } catch (e) {
      trackEvent('simulator', 'Business Creation Failed', { businessType: body.businessType, reason: (e as Error).message });
      setBizStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [api, loadEntities, loadEntityData]);

  const checkLimitations = useCallback(async () => {
    const eid = pendingEntityId.current || entityId;
    if (!eid) return;
    setBizStatus({ kind: 'pending', node: 'Fetching limitations…' });
    try {
      const limitations = await api('GET', '/limitations', undefined, { entityId: eid });
      const domestic = limitations.canSendDomesticPayment || {};
      setBizStatus({
        kind: domestic.allowed ? 'ok' : 'err',
        node: domestic.allowed
          ? 'canSendDomesticPayment = true — this business can send domestic payments.'
          : `canSendDomesticPayment = false · missing: [${(domestic.missingFields || []).join(', ')}] — see request log.`,
      });
    } catch (e) {
      setBizStatus({ kind: 'err', node: (e as Error).message });
    }
  }, [api, entityId]);

  // ---- stage-enter side effects ------------------------------------------
  useEffect(() => {
    if (!connected) return;
    if (view === 'payments' && payMode === 'detail') refreshPaymentStatus();
    else if (view === 'entity' && entityId) loadEntityDetail(entityId);
  }, [view, payMode, connected, entityId, loadEntityDetail]);

  // ---- close entity menu on outside click ---------------------------------
  useEffect(() => {
    if (!entityMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (entitySwitchRef.current && !entitySwitchRef.current.contains(e.target as Node)) setEntityMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setEntityMenuOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [entityMenuOpen]);

  // ---- render helpers -----------------------------------------------------
  const StatusLine = ({ s }: { s: Status }) =>
    s ? (
      <div className={'status show ' + s.kind}>
        {ST_ICONS[s.kind] && <span className="st-ic">{ST_ICONS[s.kind]}</span>}
        <span>{s.node}</span>
      </div>
    ) : (
      <div className="status" />
    );

  const Fact = ({ label, value }: { label: string; value: string }) => (
    <div className="pd-fact">
      <div className="pdf-l">{label}</div>
      <div className="pdf-v">{value}</div>
    </div>
  );

  const JsonPre = ({ value }: { value: unknown }) => (
    <pre dangerouslySetInnerHTML={{ __html: highlightJson(typeof value === 'string' ? value : JSON.stringify(value, null, 2)) }} />
  );

  const EmptyBlock = ({ t, s }: { t: string; s: string }) => (
    <div className="empty-block">
      <div className="eb-ic">
        <IcEmptyBox />
      </div>
      <div className="eb-t">{t}</div>
      <div className="eb-s">{s}</div>
    </div>
  );

  const LoadingBlock = ({ label = 'Loading…' }: { label?: string }) => (
    <div className="empty-block">
      <div className="eb-ic" style={{ color: 'var(--accent)' }}>
        <svg width="22" height="22" viewBox="0 0 16 16" {...S} style={spin}>
          <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" opacity=".25" />
          <path d="M8 2a6 6 0 016 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </div>
      <div className="eb-t">{label}</div>
    </div>
  );
  const Spinner = ({ size = 16 }: { size?: number }) => (
    <svg width={size} height={size} viewBox="0 0 16 16" {...S} style={{ ...spin, color: 'var(--accent)' }}>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.6" opacity=".25" />
      <path d="M8 2a6 6 0 016 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );

  const whoOf = (p: PaymentRow): string => {
    const ext = external.find((a) => a.id === p.receivingAccountId);
    return ext ? ext.counterPartyName || EXTERNAL_LABELS[ext.type || ''] || ext.type || '—' : p.receivingAccountId || '—';
  };

  const PaymentRowBtn = ({ p }: { p: PaymentRow }) => {
    const ext = external.find((a) => a.id === p.receivingAccountId);
    const who = whoOf(p);
    const via = ext ? EXTERNAL_LABELS[ext.type || ''] || ext.type : p.deliveryPreference || '';
    return (
      <button className="pay-row" type="button" onClick={() => openPayment(p.id)}>
        <span className="pay-av" style={{ background: avatarColor(who) }}>
          {initials(who)}
        </span>
        <span className="pay-who">
          <span className="pay-name">{who}</span>
          <span className="pay-sub">
            {via} · {p.deliveryPreference || 'standard-ach'}
          </span>
        </span>
        <span className="pay-date">{fmtDate(p.deductionDate)}</span>
        <span className={'pill ' + pillForStatus(p.status || '')}>
          <span className="pdot" />
          {p.status || 'scheduled'}
        </span>
        <span className="pay-amt">{money(p.amount)}</span>
        <IcRowChev />
      </button>
    );
  };

  // rail / nav helpers
  const cur = entities.find((e) => e.id === entityId);
  const currentPayment = payments.find((p) => p.id === currentPaymentId) || ({} as PaymentRow);
  const recent = payments.slice().reverse().slice(0, 4);

  const NavItem = ({ name, icon, label, count }: { name: View; icon: ReactNode; label: string; count?: number }) => {
    const disabled = (name === 'internal' || name === 'external' || name === 'payments' || name === 'entity') && !entityId;
    return (
      <button className={'nav-item' + (view === name ? ' active' : '')} type="button" disabled={disabled} onClick={() => showView(name)}>
        <span className="ni-ic">{icon}</span>
        <span className="ni-label">{label}</span>
        {count !== undefined && <span className="ni-count">{count}</span>}
      </button>
    );
  };

  // ========================================================================
  const accInternal = view === 'internal';
  const acctList = accInternal ? internal : external;

  return (
    <div
      style={
        embedded
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }
          : { height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }
      }
    >
      {!embedded && <TopNav />}
      <div className={'sim-root' + (embedded ? ' embedded' : '')}>
        <div className="sim-shell">
          {/* Sub-header: entity switcher + connection */}
          <div className="sim-subhead">
            <div className="entity-switch" ref={entitySwitchRef}>
              <button
                className="es-trigger"
                type="button"
                aria-haspopup="listbox"
                aria-expanded={entityMenuOpen}
                onClick={() => setEntityMenuOpen((o) => !o)}
              >
                <span className="es-avatar" style={{ background: cur ? avatarColor(entityName) : 'var(--neutral-300)' }}>
                  {cur ? initials(entityName) : '—'}
                </span>
                <span className="es-labels">
                  <span className="es-name">{cur ? entityName : 'Select a business'}</span>
                  <span className="es-id">{cur ? cur.id : '—'}</span>
                </span>
                <IcChevDown />
              </button>
              {entityMenuOpen && (
                <div className="es-menu" role="listbox">
                  <div className="es-menu-head">Businesses</div>
                  <div className="es-list">
                    {entitiesLoading && !entities.length ? (
                      <div className="es-empty">Loading businesses…</div>
                    ) : entities.length ? (
                      entities.map((e) => {
                        const n = nameOf(e);
                        const sel = e.id === entityId;
                        return (
                          <button
                            className={'es-opt' + (sel ? ' selected' : '')}
                            type="button"
                            key={e.id}
                            onClick={() => selectEntity(e.id)}
                          >
                            <span className="es-avatar sm" style={{ background: avatarColor(n) }}>
                              {initials(n)}
                            </span>
                            <span className="es-labels">
                              <span className="es-name">{n}</span>
                              <span className="es-id">{e.id}</span>
                            </span>
                            {sel && (
                              <span className="es-check">
                                <IcTick size={16} />
                              </span>
                            )}
                          </button>
                        );
                      })
                    ) : (
                      <div className="es-empty">No businesses yet</div>
                    )}
                  </div>
                  <button
                    className="es-create"
                    type="button"
                    onClick={() => {
                      setEntityMenuOpen(false);
                      showView('new-entity');
                    }}
                  >
                    <span className="esc-ic">
                      <IcPlus />
                    </span>
                    Create new business
                  </button>
                </div>
              )}
            </div>

            <span style={{ flex: 1 }} />
            <div className="sim-conn">
              <span className="risk-pill" title="Risk session for POST calls">
                <span className={'rdot' + (riskState === 'idle' ? '' : ' ' + riskState)} />
                <span>
                  {{ idle: 'Risk session', pending: 'Risk session…', ok: 'Risk session ready', err: 'Risk session failed' }[riskState]}
                </span>
              </span>
              {/* The env this console's partner key authenticated against. The partner name isn't
                  repeated here — it adds nothing the user doesn't already know. "not connected"
                  stays, since the dot is decorative and doesn't track connection state. */}
              <span className="env-pill">
                <span className="dot" />
                <span>{envLabel}</span>
                {!connected && (
                  <>
                    · <span className="mono">not connected</span>
                  </>
                )}
              </span>
            </div>
          </div>

          {/* 3-column console */}
          <div className={'sim-body' + (inspOpen ? '' : ' insp-closed')}>
            {/* LEFT RAIL — dashboard nav */}
            <nav className="sim-rail">
              <NavItem name="overview" icon={<IcOverview />} label="Overview" />
              <NavItem name="entity" icon={<IcBuilding />} label="Business details" />
              <div className="rail-divider" />
              <div className="rail-group-label">Money movement</div>
              <NavItem name="internal" icon={<IcBank />} label="Internal accounts" count={internal.length} />
              <NavItem name="external" icon={<IcWireOut />} label="External accounts" count={external.length} />
              <NavItem name="payments" icon={<IcPayments />} label="Payments" count={payments.length} />
              <div className="rail-spacer" />
              <div className="rail-foot">
                <button className="disconnect-btn" type="button" onClick={disconnect}>
                  <IcDisconnect />
                  Disconnect
                </button>
              </div>
            </nav>

            {/* MAIN */}
            <main className="sim-main" ref={mainRef}>
              <div className="work">
                {/* VIEW: OVERVIEW */}
                <section className={'view' + (view === 'overview' ? ' active' : '')}>
                  {!entityId && entitiesLoading ? (
                    <>
                      <div className="view-head">
                        <div>
                          <span className="eyebrow">Payer</span>
                          <h2>Loading…</h2>
                          <p>Fetching businesses for this partner.</p>
                        </div>
                      </div>
                      <div className="sim-card">
                        <LoadingBlock label="Loading businesses…" />
                      </div>
                    </>
                  ) : !entityId ? (
                    <>
                      <div className="view-head">
                        <div>
                          <span className="eyebrow">Payer</span>
                          <h2>No business selected</h2>
                          <p>Create a business to start moving money.</p>
                        </div>
                      </div>
                      <div className="sim-card empty-card">
                        <div className="ec-t">No businesses yet</div>
                        <div className="ec-s">Create your first business entity to begin.</div>
                        <button className="btn btn-primary" type="button" style={{ marginTop: 16 }} onClick={() => showView('new-entity')}>
                          Create a business
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="ov-hero">
                        <span className="ov-avatar" style={{ background: avatarColor(entityName) }}>
                          {initials(entityName)}
                        </span>
                        <div className="ov-hero-main">
                          <h2>{entityName}</h2>
                          <div className="ov-hero-meta">
                            {idtag(cur?.id || '')}
                            <span className="ov-dot">·</span>
                            {cur?.businessType || 'business'}
                            {cur?.legalName && (
                              <>
                                <span className="ov-dot">·</span>
                                {cur.legalName}
                              </>
                            )}
                          </div>
                        </div>
                        <button className="btn btn-primary" type="button" onClick={newPayment}>
                          <IcPlus color="#fff" />
                          New payment
                        </button>
                      </div>
                      <div className="stat-grid">
                        <div className="stat-tile clickable" role="button" onClick={() => showView('internal')}>
                          <span className="st-ic-box">
                            <IcStatIn />
                          </span>
                          <div className="st-val">{accountsLoading ? <Spinner size={20} /> : internal.length}</div>
                          <div className="st-lab">Internal accounts</div>
                        </div>
                        <div className="stat-tile clickable" role="button" onClick={() => showView('external')}>
                          <span className="st-ic-box">
                            <IcStatOut />
                          </span>
                          <div className="st-val">{accountsLoading ? <Spinner size={20} /> : external.length}</div>
                          <div className="st-lab">External accounts</div>
                        </div>
                        <div className="stat-tile clickable" role="button" onClick={() => showView('payments')}>
                          <span className="st-ic-box">
                            <IcStatPay />
                          </span>
                          <div className="st-val">{paymentsLoading ? <Spinner size={20} /> : payments.length}</div>
                          <div className="st-lab">Payments</div>
                        </div>
                      </div>
                      <div className="sim-card">
                        <div className="list-toolbar">
                          <span className="lt-label">Recent payments</span>
                          {!paymentsLoading && payments.length > 0 && (
                            <button className="link-more" type="button" onClick={() => showView('payments')}>
                              View all →
                            </button>
                          )}
                        </div>
                        {paymentsLoading ? (
                          <LoadingBlock label="Loading payments…" />
                        ) : recent.length ? (
                          <div className="pay-list flush">
                            {recent.map((p) => (
                              <PaymentRowBtn p={p} key={p.id} />
                            ))}
                          </div>
                        ) : (
                          <EmptyBlock t="No payments yet" s="Payouts made by this business will show up here." />
                        )}
                      </div>
                    </>
                  )}
                </section>

                {/* VIEW: BUSINESS DETAILS (entity) */}
                <section className={'view' + (view === 'entity' ? ' active' : '')}>
                  <div className="view-head">
                    <div>
                      <span className="eyebrow">Payer</span>
                      <h2>Business details</h2>
                      <p>
                        View this business's profile and update it. Legal name, legal address and tax id are fixed once the entity is
                        created.
                      </p>
                    </div>
                    {!entityEditMode && entityDetail && (
                      <button className="btn btn-primary" type="button" onClick={openEntityEdit}>
                        Edit business
                      </button>
                    )}
                  </div>

                  {entityDetailLoading ? (
                    <div className="sim-card">
                      <LoadingBlock label="Loading business…" />
                    </div>
                  ) : !entityDetail ? (
                    <div className="sim-card">
                      <EmptyBlock t="Couldn't load business" s="Reopen this page to try again." />
                    </div>
                  ) : !entityEditMode ? (
                    <div className="sim-card">
                      <div className="section-label">Profile</div>
                      <div className="pd-facts">
                        <Fact label="Business id" value={entityDetail.id} />
                        <Fact label="External id" value={entityDetail.externalId || '—'} />
                        <Fact label="DBA name" value={entityDetail.name || '—'} />
                        <Fact label="Legal name" value={entityDetail.legalName || '—'} />
                        <Fact label="Business type" value={entityDetail.businessType || '—'} />
                        <Fact
                          label="Tax id"
                          value={entityDetail.taxInfo ? `${entityDetail.taxInfo.type} ···· ${entityDetail.taxInfo.identifierLast4}` : '—'}
                        />
                        <Fact label="Phone number" value={entityDetail.phoneNumber || '—'} />
                        <Fact label="Website" value={entityDetail.website || '—'} />
                        <Fact label="Description" value={entityDetail.description || '—'} />
                        <Fact label="Industry (NAICS)" value={entityDetail.industry?.naicsCode || '—'} />
                        <Fact
                          label="Contact"
                          value={entityDetail.contact ? `${entityDetail.contact.firstName} ${entityDetail.contact.lastName}` : '—'}
                        />
                        <Fact
                          label="Owner"
                          value={
                            entityDetail.owner
                              ? `${entityDetail.owner.firstName} ${entityDetail.owner.lastName} · ${entityDetail.owner.email}`
                              : '—'
                          }
                        />
                        <Fact label="Address" value={fmtAddress(entityDetail.address)} />
                        <Fact label="Legal address" value={fmtAddress(entityDetail.legalAddress)} />
                        <Fact label="Created" value={fmtDate(entityDetail.createdAt)} />
                        <Fact label="Updated" value={fmtDate(entityDetail.updatedAt)} />
                      </div>
                    </div>
                  ) : (
                    <div className="sim-card">
                      <form ref={entityEditForm}>
                        <div className="section-label">Business profile</div>
                        <div className="row">
                          <div className="field">
                            <label>DBA name</label>
                            <input name="name" defaultValue={entityDetail.name || ''} />
                          </div>
                          <div className="field">
                            <label>Business type</label>
                            <select name="businessType" defaultValue={entityDetail.businessType || 'llc'}>
                              <option value="llc">llc</option>
                              <option value="corporation">corporation</option>
                              <option value="partnership">partnership</option>
                              <option value="sole-proprietorship">sole-proprietorship</option>
                              <option value="trust">trust</option>
                              <option value="non-profit">non-profit</option>
                              <option value="municipality">municipality</option>
                            </select>
                          </div>
                        </div>
                        <div className="row">
                          <div className="field">
                            <label>Phone number</label>
                            <input name="phoneNumber" defaultValue={entityDetail.phoneNumber || ''} />
                          </div>
                          <div className="field">
                            <label>Website</label>
                            <input name="website" defaultValue={entityDetail.website || ''} />
                          </div>
                        </div>
                        <div className="field">
                          <label>Description</label>
                          <input name="description" defaultValue={entityDetail.description || ''} />
                        </div>

                        <div className="section-label mt">Industry &amp; contact</div>
                        <div className="row-3">
                          <div className="field">
                            <label>NAICS code</label>
                            <input name="naicsCode" defaultValue={entityDetail.industry?.naicsCode || ''} />
                          </div>
                          <div className="field">
                            <label>Contact first name</label>
                            <input name="contactFirst" defaultValue={entityDetail.contact?.firstName || ''} />
                          </div>
                          <div className="field">
                            <label>Contact last name</label>
                            <input name="contactLast" defaultValue={entityDetail.contact?.lastName || ''} />
                          </div>
                        </div>

                        <div className="section-label mt">Business address</div>
                        <div className="row">
                          <div className="field">
                            <label>Line 1</label>
                            <input name="line1" defaultValue={entityDetail.address?.line1 || ''} />
                          </div>
                          <div className="field">
                            <label>Line 2 (optional)</label>
                            <input name="line2" defaultValue={entityDetail.address?.line2 || ''} />
                          </div>
                        </div>
                        <div className="row-3">
                          <div className="field">
                            <label>City</label>
                            <input name="city" defaultValue={entityDetail.address?.city || ''} />
                          </div>
                          <div className="field">
                            <label>State</label>
                            <input name="state" defaultValue={entityDetail.address?.state || ''} />
                          </div>
                          <div className="field">
                            <label>Postal code</label>
                            <input name="postalCode" defaultValue={entityDetail.address?.postalCode || ''} />
                          </div>
                        </div>
                        <div className="field">
                          <label>Apt / suite number (optional)</label>
                          <input name="aptNumber" defaultValue={entityDetail.address?.aptNumber || ''} />
                        </div>

                        <div className="section-label mt">External id</div>
                        <div className="field">
                          <label>Partner's own id for this entity</label>
                          <input name="externalId" defaultValue={entityDetail.externalId || ''} />
                        </div>

                        <div className="section-label mt">Owner</div>
                        <div className="row">
                          <div className="field">
                            <label>First name</label>
                            <input name="ownerFirst" defaultValue={entityDetail.owner?.firstName || ''} />
                          </div>
                          <div className="field">
                            <label>Last name</label>
                            <input name="ownerLast" defaultValue={entityDetail.owner?.lastName || ''} />
                          </div>
                        </div>
                        <div className="row-3">
                          <div className="field">
                            <label>Email</label>
                            <input name="ownerEmail" defaultValue={entityDetail.owner?.email || ''} />
                          </div>
                          <div className="field">
                            <label>Date of birth</label>
                            <input name="ownerDob" placeholder="YYYY-MM-DD" />
                          </div>
                          <div className="field">
                            <label>Phone</label>
                            <input name="ownerPhone" placeholder="+12125559876" />
                          </div>
                        </div>
                      </form>
                      <div className="actions">
                        <button className="btn btn-primary" type="button" onClick={saveEntityEdit}>
                          Save changes
                        </button>
                        <span className="spacer" />
                        <button className="btn btn-ghost" type="button" onClick={() => setEntityEditMode(false)}>
                          Cancel
                        </button>
                      </div>
                      <StatusLine s={entityUpdateStatus} />
                    </div>
                  )}
                </section>

                {/* VIEW: ACCOUNTS (internal / external) */}
                <section className={'view' + (view === 'internal' || view === 'external' ? ' active' : '')}>
                  <div className="view-head">
                    <div>
                      <span className="eyebrow">Money movement</span>
                      <h2>{accInternal ? 'Internal accounts' : 'External accounts'}</h2>
                      <p>
                        {accInternal
                          ? 'Where money is pulled from — bank accounts, cards or linked sources.'
                          : 'How the recipient gets paid — bank transfer, check, wire or virtual card.'}
                      </p>
                    </div>
                  </div>

                  {acctMode === 'list' ? (
                    <div>
                      <div className="list-toolbar">
                        <span className="lt-label">{accInternal ? 'Internal accounts' : 'External accounts'}</span>
                        <button className="btn btn-primary btn-sm" type="button" onClick={openAddAccount}>
                          <IcPlus color="#fff" />
                          {accInternal ? 'Add internal account' : 'Add external account'}
                        </button>
                      </div>
                      <div className="acct-list">
                        {accountsLoading ? (
                          <LoadingBlock label={accInternal ? 'Loading internal accounts…' : 'Loading external accounts…'} />
                        ) : acctList.length ? (
                          acctList.map((a) => {
                            const kindLabel = INTERNAL_LABELS[a.type || ''] || a.type;
                            const name = accInternal ? a.nickname || kindLabel : a.counterPartyName || a.type;
                            const last4 = accountLast4(a);
                            const routing = a.bankAccount?.routingNumber;
                            const sub = accInternal
                              ? a.type === 'ach'
                                ? [routing ? `Routing ${routing}` : null, last4 ? `···· ${last4}` : null].filter(Boolean).join(' · ') ||
                                  kindLabel
                                : `${kindLabel}${last4 ? ' ···· ' + last4 : ''}`
                              : [EXTERNAL_LABELS[a.type || ''] || a.type, a.counterPartyEmail].filter(Boolean).join(' · ');
                            return (
                              <div
                                className="acct-row"
                                key={a.id}
                                role="button"
                                tabIndex={0}
                                style={{ cursor: 'pointer' }}
                                onClick={() => openAccountDetail(a.id)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    openAccountDetail(a.id);
                                  }
                                }}
                              >
                                <span className="ar-ic">{accInternal ? <IcBank size={19} /> : <DeliveryIcon type={a.type || ''} />}</span>
                                <div className="ar-main">
                                  <div className="ar-name">{name}</div>
                                  <div className="ar-sub">{sub}</div>
                                </div>
                                {accInternal && a.isVerified === false && (
                                  <>
                                    <span className="pill warning">
                                      <span className="pdot" />
                                      Unverified
                                    </span>
                                    <button
                                      className="btn btn-ghost btn-sm"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        startVerifyAccount(a.id);
                                      }}
                                    >
                                      Verify
                                    </button>
                                  </>
                                )}
                                <span className="tag-chip">{a.type}</span>
                                <span className="ar-id">{shortId(a.id)}</span>
                                <IcRowChev />
                              </div>
                            );
                          })
                        ) : (
                          <EmptyBlock
                            t={accInternal ? 'No internal accounts' : 'No external accounts'}
                            s={accInternal ? 'Add a bank account to pull money from.' : 'Add a way for recipients to get paid.'}
                          />
                        )}
                      </div>
                      {accInternal && verifyingAccountId && (
                        <div className="sim-card acct-form" style={{ marginTop: 16 }}>
                          <div className="form-head">
                            <h3>Verify account {shortId(verifyingAccountId)}</h3>
                          </div>
                          <StatusLine s={verifyStatus} />
                          {verifyLinkUrl && (
                            <div>
                              <LinkUrlRow url={verifyLinkUrl} />
                              <iframe className="link-iframe" src={verifyLinkUrl} title="Verify account link" />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : acctMode === 'detail' ? (
                    /* Account detail (GET /accounts/{id}) */
                    <div className="sim-card acct-form">
                      <div className="form-head">
                        <button className="link-back" type="button" onClick={() => setAcctMode('list')}>
                          <IcBack />
                          {accInternal ? 'Internal accounts' : 'External accounts'}
                        </button>
                        <h3>Account detail</h3>
                      </div>
                      {accountDetailLoading ? (
                        <LoadingBlock label="Loading account…" />
                      ) : accountDetail ? (
                        (() => {
                          const d = accountDetail;
                          const isInternal = d.ownershipType === 'internal';
                          const typeLabel = (isInternal ? INTERNAL_LABELS : EXTERNAL_LABELS)[d.type || ''] || d.type || '—';
                          const title = isInternal ? d.nickname || typeLabel : d.counterPartyName || typeLabel;
                          const meta = Object.entries(d.metadata || {});
                          return (
                            <>
                              <div className="pd-hero" style={{ marginBottom: 4 }}>
                                <div>
                                  <div className="pd-amt" style={{ fontSize: 22 }}>
                                    {title}
                                  </div>
                                  <div className="pd-to">
                                    {typeLabel} · {idtag(d.id)}
                                  </div>
                                </div>
                                {isInternal && d.isVerified !== undefined && (
                                  <span className={'pill ' + (d.isVerified ? 'success' : 'warning')}>
                                    <span className="pdot" />
                                    {d.isVerified ? 'Verified' : 'Unverified'}
                                  </span>
                                )}
                              </div>
                              <div className="pd-facts">
                                <Fact label="Ownership" value={d.ownershipType || '—'} />
                                <Fact label="Type" value={typeLabel} />
                                {d.bankAccount && (
                                  <>
                                    <Fact label="Account type" value={d.bankAccount.accountType || '—'} />
                                    <Fact label="Routing number" value={d.bankAccount.routingNumber || '—'} />
                                    <Fact
                                      label="Account number"
                                      value={d.bankAccount.accountNumberLast4 ? '···· ' + d.bankAccount.accountNumberLast4 : '—'}
                                    />
                                  </>
                                )}
                                {d.card && (
                                  <>
                                    <Fact label="Network" value={d.card.network || '—'} />
                                    <Fact label="Card number" value={d.card.lastFourDigits ? '···· ' + d.card.lastFourDigits : '—'} />
                                    <Fact label="Expiration" value={d.card.expiration || '—'} />
                                  </>
                                )}
                                {d.domesticWire && (
                                  <>
                                    <Fact label="Routing number" value={d.domesticWire.routingNumber || '—'} />
                                    <Fact
                                      label="Account number"
                                      value={d.domesticWire.accountNumberLast4 ? '···· ' + d.domesticWire.accountNumberLast4 : '—'}
                                    />
                                    <Fact label="Address" value={fmtAddress(d.domesticWire.address) || '—'} />
                                  </>
                                )}
                                {d.paperCheck && (
                                  <>
                                    <Fact label="Print name" value={d.paperCheck.printName || '—'} />
                                    <Fact label="Address" value={fmtAddress(d.paperCheck.address) || '—'} />
                                  </>
                                )}
                                {(d.virtualCard?.counterPartyEmail || d.counterPartyEmail) && (
                                  <Fact label="Recipient email" value={d.virtualCard?.counterPartyEmail || d.counterPartyEmail || '—'} />
                                )}
                                <Fact label="External id" value={d.externalId || '—'} />
                                <Fact label="Created" value={fmtDate(d.createdAt)} />
                                <Fact label="Updated" value={fmtDate(d.updatedAt)} />
                                {meta.map(([k, v]) => (
                                  <Fact key={k} label={`metadata.${k}`} value={String(v)} />
                                ))}
                              </div>
                              {accInternal && d.isVerified === false && (
                                <div className="actions">
                                  <button className="btn btn-ghost btn-sm" type="button" onClick={() => startVerifyAccount(d.id)}>
                                    Verify account
                                  </button>
                                </div>
                              )}
                              {accInternal && verifyingAccountId === d.id && verifyLinkUrl && (
                                <div style={{ marginTop: 12 }}>
                                  <StatusLine s={verifyStatus} />
                                  <LinkUrlRow url={verifyLinkUrl} />
                                  <iframe className="link-iframe" src={verifyLinkUrl} title="Verify account link" />
                                </div>
                              )}
                              <div className="actions" style={{ marginTop: 8 }}>
                                <button className="btn btn-danger" type="button" onClick={() => deleteAccount(d.id)}>
                                  Delete account
                                </button>
                              </div>
                              <p className="hint">
                                An account referenced by any payment can't be deleted (<code>409 ACCOUNT_IN_USE</code>).
                              </p>
                              <StatusLine s={accountActionStatus} />
                            </>
                          );
                        })()
                      ) : (
                        <>
                          <EmptyBlock t="Account not found" s="It may have been deleted, or the request failed — see the log." />
                          <StatusLine s={accountActionStatus} />
                        </>
                      )}
                    </div>
                  ) : accInternal ? (
                    /* Add internal account */
                    <div className="sim-card acct-form">
                      <div className="form-head">
                        <button className="link-back" type="button" onClick={() => setAcctMode('list')}>
                          <IcBack />
                          Internal accounts
                        </button>
                        <h3>Add internal account</h3>
                      </div>
                      <div className="section-label">How money comes in</div>
                      <div className="seg-group c2">
                        {(
                          [
                            ['plaid', <IcPlaid key="p" />, 'Plaid', 'Connect via link'],
                            ['card', <IcCard key="c" />, 'Card', 'Tokenized via link'],
                          ] as [string, ReactNode, string, string][]
                        ).map(([t, ic, title, desc]) => (
                          <div className={'seg-opt' + (newFundType === t ? ' selected' : '')} key={t} onClick={() => selectFundingType(t)}>
                            <span className="so-ic">{ic}</span>
                            <div>
                              <div className="so-t">{title}</div>
                              <div className="so-d">{desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>

                      <p className="hint">
                        Generate a scoped {newFundType} link, then complete it in the portal frame below. The frame reports back via
                        postMessage when it's done — no need to refresh.
                      </p>
                      <div className="actions">
                        <button className="btn btn-primary" type="button" onClick={generateLink}>
                          Generate link
                        </button>
                        {showPollBtn && (
                          <button className="btn btn-ghost" type="button" onClick={pollInternalOnce}>
                            Didn't update? Check manually
                          </button>
                        )}
                      </div>
                      {linkUrl && (
                        <div>
                          <LinkUrlRow url={linkUrl} />
                          <iframe className="link-iframe" src={linkUrl} title="Scoped account link" />
                        </div>
                      )}
                      <StatusLine s={fundStatus} />
                    </div>
                  ) : (
                    /* Add external account */
                    <div className="sim-card acct-form">
                      <div className="form-head">
                        <button className="link-back" type="button" onClick={() => setAcctMode('list')}>
                          <IcBack />
                          External accounts
                        </button>
                        <h3>Add external account</h3>
                      </div>
                      <div className="section-label">How they get paid</div>
                      <div className="seg-group c3">
                        {(
                          [
                            ['check', <IcCheck key="c" />, 'Paper check', 'Printed & mailed'],
                            ['domestic-wire', <IcWire key="w" />, 'Domestic wire', 'Same-day rails'],
                            ['virtual-card', <IcVCard key="v" />, 'Virtual card', 'Emailed to recipient'],
                          ] as [string, ReactNode, string, string][]
                        ).map(([t, ic, title, desc]) => (
                          <div className={'seg-opt' + (newDelivType === t ? ' selected' : '')} key={t} onClick={() => setNewDelivType(t)}>
                            <span className="so-ic">{ic}</span>
                            <div>
                              <div className="so-t">{title}</div>
                              <div className="so-d">{desc}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <form ref={externalForm}>
                        <div className="row">
                          <div className="field">
                            <label>Counterparty name</label>
                            <input name="counterPartyName" defaultValue="Globex Manufacturing" />
                          </div>
                          <div className="field">
                            <label>Counterparty email</label>
                            <input name="counterPartyEmail" type="email" defaultValue="ap@globex.example.com" />
                          </div>
                        </div>

                        {newDelivType === 'check' && (
                          <div className="ext-fields">
                            <div className="row">
                              <div className="field">
                                <label>Address line 1</label>
                                <input name="checkLine1" defaultValue="1 Rocket Rd" />
                              </div>
                              <div className="field">
                                <label>Line 2 (optional)</label>
                                <input name="checkLine2" defaultValue="" />
                              </div>
                            </div>
                            <div className="row-3">
                              <div className="field">
                                <label>City</label>
                                <input name="checkCity" defaultValue="Hawthorne" />
                              </div>
                              <div className="field">
                                <label>State</label>
                                <input name="checkState" defaultValue="CA" />
                              </div>
                              <div className="field">
                                <label>Postal code</label>
                                <input name="checkPostal" defaultValue="90250" />
                              </div>
                            </div>
                            <div className="field">
                              <label>Apt / suite number (optional)</label>
                              <input name="checkApt" defaultValue="" />
                            </div>
                          </div>
                        )}

                        {newDelivType === 'domestic-wire' && (
                          <div className="ext-fields">
                            <div className="row">
                              <div className="field">
                                <label>Account number</label>
                                <input name="wireAccountNumber" defaultValue="987654321" />
                              </div>
                              <div className="field">
                                <label>Routing number (ABA)</label>
                                <input name="wireRoutingNumber" defaultValue="021000021" />
                              </div>
                            </div>
                            <div className="field">
                              <label>Address line 1</label>
                              <input name="wireLine1" defaultValue="1 Rocket Rd" />
                            </div>
                            <div className="row-3">
                              <div className="field">
                                <label>City</label>
                                <input name="wireCity" defaultValue="Hawthorne" />
                              </div>
                              <div className="field">
                                <label>State</label>
                                <input name="wireState" defaultValue="CA" />
                              </div>
                              <div className="field">
                                <label>Postal code</label>
                                <input name="wirePostal" defaultValue="90250" />
                              </div>
                            </div>
                          </div>
                        )}

                        {newDelivType === 'virtual-card' && (
                          <div className="ext-fields">
                            <p className="hint">The single-use virtual card is delivered to the counterparty email above.</p>
                          </div>
                        )}
                      </form>
                      <div className="actions">
                        <button className="btn btn-primary" type="button" onClick={createExternal}>
                          Create external account
                        </button>
                      </div>
                      <StatusLine s={delivStatus} />
                    </div>
                  )}
                </section>

                {/* VIEW: PAYMENTS */}
                <section className={'view' + (view === 'payments' ? ' active' : '')}>
                  {payMode === 'list' && (
                    <>
                      <div className="view-head">
                        <div>
                          <span className="eyebrow">Money movement</span>
                          <h2>Payments</h2>
                          <p>Every payout made by this business. Create one to tie an internal account to an external account.</p>
                        </div>
                        <button className="btn btn-primary" type="button" onClick={newPayment}>
                          <IcPlus color="#fff" />
                          New payment
                        </button>
                      </div>
                      {paymentsLoading ? (
                        <div className="sim-card">
                          <LoadingBlock label="Loading payments…" />
                        </div>
                      ) : payments.length ? (
                        <div className="sim-card flush-card">
                          <div className="pay-list flush">
                            {payments
                              .slice()
                              .reverse()
                              .map((p) => (
                                <PaymentRowBtn p={p} key={p.id} />
                              ))}
                          </div>
                          {paymentsHasMore && (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 0' }}>
                              <button
                                className="btn btn-secondary btn-sm"
                                type="button"
                                onClick={loadMorePayments}
                                disabled={paymentsLoadingMore}
                              >
                                {paymentsLoadingMore ? <Spinner size={16} /> : 'Load more'}
                              </button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="sim-card">
                          <EmptyBlock t="No payments yet" s="Create a payment to tie an internal account to an external account." />
                        </div>
                      )}
                    </>
                  )}

                  {payMode === 'create' && (
                    <div className="sim-card pay-form">
                      <div className="form-head">
                        <button className="link-back" type="button" onClick={() => setPayMode('list')}>
                          <IcBack />
                          Payments
                        </button>
                        <h3>New payment</h3>
                      </div>
                      <div>
                        <div className="row">
                          <div className="field">
                            <label>Internal account (funding)</label>
                            <select value={pay.originating} onChange={(e) => setPay((p) => ({ ...p, originating: e.target.value }))}>
                              {internal.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {(a.nickname || INTERNAL_LABELS[a.type || ''] || a.type) + ' — ' + shortId(a.id)}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="field">
                            <label>External account (delivery)</label>
                            <select value={pay.receiving} onChange={(e) => setPay((p) => ({ ...p, receiving: e.target.value }))}>
                              {external.map((a) => (
                                <option key={a.id} value={a.id}>
                                  {(a.counterPartyName || EXTERNAL_LABELS[a.type || ''] || a.type) +
                                    ` (${EXTERNAL_LABELS[a.type || ''] || a.type}) — ` +
                                    shortId(a.id)}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="row-3">
                          <div className="field">
                            <label>Amount (USD)</label>
                            <input value={pay.amount} onChange={(e) => setPay((p) => ({ ...p, amount: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>Delivery speed</label>
                            <select value={effPref} onChange={(e) => setPay((p) => ({ ...p, preference: e.target.value }))}>
                              {payPrefs.map((p) => {
                                const ineligible = fastPrefIneligible(p);
                                return (
                                  <option key={p} value={p} disabled={ineligible}>
                                    {p}
                                    {ineligible ? ' — not eligible' : ''}
                                  </option>
                                );
                              })}
                            </select>
                            <span className="hint">
                              {fastEligLoading
                                ? 'Checking fast-payment eligibility…'
                                : fastEligError
                                  ? 'Eligibility check unavailable — standard speed only.'
                                  : 'Faster options are enabled per /tools/fast-payment-eligibility.'}
                            </span>
                          </div>
                          <div className="field">
                            <label>Deduction date</label>
                            <input value={pay.deductionDate} onChange={(e) => setPay((p) => ({ ...p, deductionDate: e.target.value }))} />
                          </div>
                        </div>
                        <p className="hint">
                          {feeLoading ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <Spinner size={14} /> Estimating fee…
                            </span>
                          ) : feeError ? (
                            `Fee estimate unavailable: ${feeError}`
                          ) : !(pay.originating && pay.receiving && parseMoney(pay.amount)) ? (
                            'Pick both accounts to preview the fee.'
                          ) : feeAmount === null ? (
                            'Fee varies with the payment amount.'
                          ) : feeAmount === 0 ? (
                            'No fee for this payment.'
                          ) : (
                            `Estimated fee: ${money(feeAmount)}`
                          )}
                        </p>
                        {etaByPref[effPref] && (
                          <p className="hint">
                            Estimated delivery ({effPref}): {fmtDate(etaByPref[effPref])}
                          </p>
                        )}
                        <div className="row">
                          <div className="field">
                            <label>External id</label>
                            <input value={pay.externalId} onChange={(e) => setPay((p) => ({ ...p, externalId: e.target.value }))} />
                          </div>
                          <div className="field">
                            <label>Note to recipient</label>
                            <input value={pay.note} onChange={(e) => setPay((p) => ({ ...p, note: e.target.value }))} />
                          </div>
                        </div>
                        {originatingType === 'card' && (
                          <div className="row">
                            <div className="field">
                              <label>Merchant category code (MCC)</label>
                              <input
                                value={pay.mcc}
                                onChange={(e) => setPay((p) => ({ ...p, mcc: e.target.value }))}
                                placeholder="4-digit MCC, e.g. 5045"
                              />
                              <span className="hint">Required when paying from a card — the counterparty's 4-digit MCC.</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="actions">
                        <button
                          className="btn btn-primary"
                          type="button"
                          disabled={!pay.originating || !pay.receiving || (originatingType === 'card' && !pay.mcc)}
                          onClick={createPayment}
                        >
                          <IcArrowWhite />
                          Create payment
                        </button>
                      </div>
                      <StatusLine s={payStatus} />
                    </div>
                  )}

                  {payMode === 'detail' && (
                    <PaymentDetail
                      p={currentPayment}
                      internal={internal}
                      external={external}
                      who={whoOf(currentPayment)}
                      actionStatus={actionStatus}
                      runSimulationAction={runSimulationAction}
                      onCancel={cancelPayment}
                      cancelStatus={cancelStatus}
                      onBack={() => setPayMode('list')}
                      StatusLine={StatusLine}
                    />
                  )}
                </section>

                {/* VIEW: NEW ENTITY */}
                <section className={'view' + (view === 'new-entity' ? ' active' : '')}>
                  <div className="view-head">
                    <div>
                      <span className="eyebrow">Payer</span>
                      <h2>Create a business</h2>
                      <p>Every payout is made by a business. Create a new legal entity to onboard it on {envLabel}.</p>
                    </div>
                  </div>
                  <div className="sim-card">
                    <form ref={entityForm}>
                      <div className="section-label">Business profile</div>
                      <div className="row">
                        <div className="field">
                          <label>DBA name</label>
                          <input name="name" defaultValue="Acme Supplies" />
                        </div>
                        <div className="field">
                          <label>Legal name</label>
                          <input name="legalName" defaultValue="Acme Supplies LLC" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="field">
                          <label>Business type</label>
                          <select name="businessType" defaultValue="llc">
                            <option value="llc">llc</option>
                            <option value="corporation">corporation</option>
                            <option value="partnership">partnership</option>
                            <option value="sole-proprietorship">sole-proprietorship</option>
                            <option value="trust">trust</option>
                            <option value="non-profit">non-profit</option>
                            <option value="municipality">municipality</option>
                          </select>
                        </div>
                        <div className="field">
                          <label>Phone number</label>
                          <input name="phoneNumber" defaultValue="+12125551234" />
                        </div>
                      </div>
                      <div className="row-3">
                        <div className="field">
                          <label>Tax id type</label>
                          <select name="taxType" defaultValue="ein">
                            <option value="ein">ein</option>
                            <option value="ssn">ssn</option>
                          </select>
                        </div>
                        <div className="field">
                          <label>Tax identifier</label>
                          <input name="taxIdentifier" defaultValue="12-3456789" />
                        </div>
                        <div className="field">
                          <label>NAICS code</label>
                          <input name="naicsCode" defaultValue="424410" />
                        </div>
                      </div>
                      <div className="row">
                        <div className="field">
                          <label>Contact first name</label>
                          <input name="contactFirst" defaultValue="Jane" />
                        </div>
                        <div className="field">
                          <label>Contact last name</label>
                          <input name="contactLast" defaultValue="Doe" />
                        </div>
                      </div>
                      <div className="field">
                        <label>Website</label>
                        <input name="website" defaultValue="https://acmesupplies.com" />
                      </div>

                      <div className="section-label mt">Business address</div>
                      <div className="field">
                        <label>Line 1</label>
                        <input name="line1" defaultValue="350 5th Ave" />
                      </div>
                      <div className="row-3">
                        <div className="field">
                          <label>City</label>
                          <input name="city" defaultValue="New York" />
                        </div>
                        <div className="field">
                          <label>State</label>
                          <input name="state" defaultValue="NY" />
                        </div>
                        <div className="field">
                          <label>Postal code</label>
                          <input name="postalCode" defaultValue="10118" />
                        </div>
                      </div>

                      <div className="section-label mt">Owner</div>
                      <div className="row">
                        <div className="field">
                          <label>First name</label>
                          <input name="ownerFirst" defaultValue="Jane" />
                        </div>
                        <div className="field">
                          <label>Last name</label>
                          <input name="ownerLast" defaultValue="Doe" />
                        </div>
                      </div>
                      <div className="row-3">
                        <div className="field">
                          <label>Email</label>
                          <input name="ownerEmail" defaultValue="jane.doe@acmesupplies.com" />
                        </div>
                        <div className="field">
                          <label>Date of birth</label>
                          <input name="ownerDob" defaultValue="1985-04-12" />
                        </div>
                        <div className="field">
                          <label>Phone</label>
                          <input name="ownerPhone" defaultValue="+12125559876" />
                        </div>
                      </div>
                    </form>
                    <div className="actions">
                      <button className="btn btn-primary" type="button" onClick={createEntity}>
                        Create business
                      </button>
                      {showCheckLim && (
                        <button className="btn btn-ghost" type="button" onClick={checkLimitations}>
                          Check eligibility
                        </button>
                      )}
                      <span className="spacer" />
                      <button className="btn btn-ghost" type="button" onClick={() => showView('overview')}>
                        Cancel
                      </button>
                    </div>
                    <StatusLine s={bizStatus} />
                  </div>
                </section>
              </div>
            </main>

            {/* RIGHT INSPECTOR */}
            {inspOpen && (
              <aside className="sim-inspector">
                <div className="insp-tabs">
                  <button className="insp-tab active" type="button">
                    <IcReqTab />
                    Requests<span className="count">{reqLog.length}</span>
                  </button>
                  <button className="insp-min-btn" type="button" title="Minimize inspector" onClick={() => setInspOpen(false)}>
                    <IcMinimize />
                  </button>
                </div>
                <div className="insp-body">
                  <div className="insp-pane active">
                    <div className="insp-toolbar">
                      <span className="it-label">Request log</span>
                      <button className="it-clear" type="button" onClick={() => setReqLog([])}>
                        Clear
                      </button>
                    </div>
                    {reqLog.length === 0 ? (
                      <div className="insp-empty">
                        <div className="ie-ic">
                          <IcEmptyReq />
                        </div>
                        <div className="ie-t">No requests yet</div>
                        <div className="ie-s">Every call to the Payouts API is logged here with its request and response.</div>
                      </div>
                    ) : (
                      reqLog.map((it) => (
                        <div className={'log-item' + (it.open ? '' : ' collapsed')} key={it.id}>
                          <div
                            className="log-head"
                            onClick={() => setReqLog((prev) => prev.map((x) => (x.id === it.id ? { ...x, open: !x.open } : x)))}
                          >
                            <div className="log-head-main">
                              <span className={'log-method ' + it.method}>{it.method}</span>
                              <span className={'log-code ' + (it.ok ? 'ok' : 'err')}>{it.status}</span>
                              <span className="log-url">{it.path}</span>
                              <span className="log-time">{it.time}</span>
                            </div>
                            <IcChevron />
                          </div>
                          <div className="log-body">
                            {it.reqBody != null && <JsonPre value={it.reqBody} />}
                            {it.resBody !== undefined && <JsonPre value={it.resBody} />}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </aside>
            )}
          </div>

          {!inspOpen && (
            <button className="insp-reopen-float" type="button" title="Show inspector" onClick={() => setInspOpen(true)}>
              <IcReopen />
              {reqLog.length > 0 && <span className="count">{reqLog.length}</span>}
            </button>
          )}
        </div>

        {/* API KEY GATE */}
        {!connected && (
          <div className="gate">
            <div className="gate-card">
              <div className="g-head">
                <div className="g-ic">
                  <IcLock />
                </div>
                <h2>Connect your partner key</h2>
              </div>
              <p className="g-lede">
                Paste the Staging API key from, it can be found in the API Keys section. It authenticates every call and is held in memory
                for this session only — never stored.
              </p>
              {showEnvPicker && (
                <div className="field">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 0 }}>
                    <input
                      type="checkbox"
                      checked={useLocal}
                      onChange={(e) => setUseLocal(e.target.checked)}
                      style={{ width: 16, height: 16, accentColor: 'var(--accent)', flex: 'none' }}
                    />
                    Use local
                  </label>
                </div>
              )}
              <div className="field">
                <label>Partner API key</label>
                <input
                  className="mono"
                  type="password"
                  autoComplete="off"
                  placeholder="melio_secret_staging01_… / melio_secret_eilat_…"
                  value={gateKey}
                  onChange={(e) => setGateKey(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') connect();
                  }}
                />
                <p className="hint">{envHint}</p>
              </div>
              <div className="actions" style={{ marginTop: 18 }}>
                <button className="btn btn-primary" type="button" style={{ width: '100%', height: 48 }} onClick={connect}>
                  <IcArrowWhite size={16} w="1.7" />
                  Connect &amp; open simulator
                </button>
              </div>
              <StatusLine s={gateStatus} />
              <div className="g-note">
                <span className="gn-ic">
                  <IcInfo />
                </span>
                <span>The key is held in memory for this session only and is never stored or sent anywhere but the Melio API.</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Payment detail (hero + facts + lifecycle + simulate) ------------------
function PaymentDetail({
  p,
  internal,
  external,
  who,
  actionStatus,
  runSimulationAction,
  onCancel,
  cancelStatus,
  onBack,
  StatusLine,
}: {
  p: PaymentRow;
  internal: Account[];
  external: Account[];
  who: string;
  actionStatus: Status;
  runSimulationAction: (action: string) => void;
  onCancel: () => void;
  cancelStatus: Status;
  onBack: () => void;
  StatusLine: (props: { s: Status }) => ReactNode;
}) {
  const ext = external.find((a) => a.id === p.receivingAccountId);
  const intn = internal.find((a) => a.id === p.originatingAccountId);
  const status = p.status || 'scheduled';
  const failed = statusToStep(status) === -1;
  const cur = failed ? 99 : statusToStep(status);

  const Fact = ({ label, value }: { label: string; value: string }) => (
    <div className="pd-fact">
      <div className="pdf-l">{label}</div>
      <div className="pdf-v">{value}</div>
    </div>
  );

  return (
    <>
      <button className="link-back" type="button" style={{ marginBottom: 16 }} onClick={onBack}>
        <IcBack />
        Payments
      </button>
      <div className="pd-hero">
        <div>
          <div className="pd-amt">{money(p.amount)}</div>
          <div className="pd-to">
            to <strong>{who}</strong> · {idtag(p.id)}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span className={'pill ' + pillForStatus(status)}>
            <span className="pdot" />
            {status}
          </span>
          {status === 'scheduled' && (
            <button className="btn btn-danger btn-sm" type="button" onClick={onCancel}>
              Cancel payment
            </button>
          )}
        </div>
      </div>
      {cancelStatus && <div style={{ marginBottom: 12 }}>{StatusLine({ s: cancelStatus })}</div>}
      <div className="pd-facts">
        <Fact
          label="Internal account"
          value={intn ? intn.nickname || INTERNAL_LABELS[intn.type || ''] || '' : p.originatingAccountId || '—'}
        />
        <Fact
          label="External account"
          value={ext ? `${who} · ${EXTERNAL_LABELS[ext.type || ''] || ext.type}` : p.receivingAccountId || '—'}
        />
        <Fact label="Delivery speed" value={p.deliveryPreference || 'standard-ach'} />
        <Fact label="Deduction date" value={fmtDate(p.deductionDate)} />
        <Fact label="Delivery date" value={fmtDate(p.deliveryDate)} />
        <Fact label="Fees" value={feeTotalLabel(p.fees)} />
        <Fact label="External id" value={p.externalId || '—'} />
        <Fact label="Note" value={p.noteToRecipient || '—'} />
      </div>
      {p.fees && p.fees.length > 0 && (
        <div className="sim-card">
          <div className="section-label">Fees</div>
          <div className="acct-list">
            {p.fees.map((f, i) => {
              const pricing = feePricing(f);
              const sub = [f.chargeTo ? `charged to ${f.chargeTo}` : null, pricing || null].filter(Boolean).join(' · ');
              return (
                <div className="acct-row" key={(f.type || 'fee') + i}>
                  <div className="ar-main">
                    <div className="ar-name">
                      {f.type || 'fee'}
                      {f.status ? ` · ${f.status}` : ''}
                    </div>
                    {sub && <div className="ar-sub">{sub}</div>}
                  </div>
                  <span className="pay-amt">{typeof f.amount === 'number' ? money(f.amount) : '—'}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <div className="sim-card">
        <div className="section-label">Lifecycle</div>
        <div className="tracker">
          {LIFECYCLE.map((s, i) => {
            const done = i < cur;
            const current = i === cur;
            return (
              <div className="trk-step" key={s.key}>
                <div className="trk-rail">
                  <div className={'trk-node' + (done ? ' done' : '') + (current ? ' current' : '')}>
                    {done ? <IcTick size={12} /> : i + 1}
                  </div>
                  {i < LIFECYCLE.length - 1 && <div className={'trk-line' + (done ? ' done' : '')} />}
                </div>
                <div className="trk-body">
                  <div className="trk-t">{s.label}</div>
                  <div className="trk-s">{s.sub}</div>
                </div>
              </div>
            );
          })}
          {failed && <div className="trk-fail">Payment {status} — see the request log for details.</div>}
        </div>
      </div>
      <div className="sim-card">
        <div className="section-label">Simulate lifecycle actions</div>
        <p className="hint" style={{ margin: '-6px 0 16px' }}>
          Advance this payment through its lifecycle (non-production only). Each action hits the real payment, so status changes happen
          exactly as in production.
        </p>
        <div className="action-grid">
          {SIMULATION_ACTIONS.map((a) => (
            <button className={'btn btn-' + a.kind} type="button" key={a.action} onClick={() => runSimulationAction(a.action)}>
              {a.label}
            </button>
          ))}
        </div>
        {StatusLine({ s: actionStatus })}
      </div>
    </>
  );
}
